import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Phone,
  Mail,
  MapPin,
  Star,
  HardHat,
  Briefcase,
  DollarSign,
  Calendar as CalendarIcon,
  Award,
  Truck,
  CalendarDays,
  Activity,
  Clock,
  X,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  CalendarOff,
  Wallet,
  Building2,
  Home,
  Loader2,
  Trash2,
  UserPlus,
  FileSpreadsheet,
  Send,
  RefreshCw,
  ShieldCheck,
  Upload,
  FileText,
  Camera,
  ExternalLink,
  User,
  MoreVertical,
  UserX,
  UserCheck,
  KeyRound,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { statusColorMap, certStatusColors, DAYS } from "../data/technicianMock";
import { techniciansApi, customersApi, jobTitlesApi, usersApi, invitesApi, permissionsApi, jobsApi, billingApi, payrollApi, tradeTypesApi } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import BulkInviteModal from "../components/BulkInviteModal";
import {
  CreateUserModal,
  InviteModal,
  PermBadge,
  CopyableLink,
} from "../components/StaffModals";
import RoleButtons from "../components/RoleButtons";
import ConfirmRemoveModal from "../components/ConfirmRemoveModal";
import ActivateOneUserModal from "../components/ActivateOneUserModal";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import { Rocket, ChevronRight } from "lucide-react";
import { PAY_FREQUENCIES } from "./payroll/payrollHelpers";

// Avoid the default marker icon 404s by configuring a CSS-based divIcon
function divIcon(color, label) {
  return L.divIcon({
    html: `<div style="background:${color};color:#fff;font-weight:700;font-size:11px;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.25)">${label}</div>`,
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

const ROLE_LABELS = {
  admin: "Admin",
  dispatcher: "Dispatcher",
  technician: "Technician",
  office_personnel: "Office Personnel",
};

const ROLE_THEME = {
  admin: { border: "border-indigo-300", bg: "bg-indigo-50/30", headerBg: "bg-indigo-50", dot: "bg-indigo-500", text: "text-indigo-700" },
  dispatcher: { border: "border-blue-300", bg: "bg-blue-50/30", headerBg: "bg-blue-50", dot: "bg-blue-500", text: "text-blue-700" },
  technician: { border: "border-amber-300", bg: "bg-amber-50/30", headerBg: "bg-amber-50", dot: "bg-amber-500", text: "text-amber-700" },
  office_personnel: { border: "border-emerald-300", bg: "bg-emerald-50/30", headerBg: "bg-emerald-50", dot: "bg-emerald-500", text: "text-emerald-700" },
};

function timeAgo(iso) {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Never";
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "Just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}


export default function Technicians() {
  const { user, hasModule } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [techs, setTechs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All");
  const [selected, setSelected] = useState(null); // technician drawer
  const [editingAccount, setEditingAccount] = useState(null); // account-only login modal
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Ship 1: per-user activation (pay-per-activated-user model).
  const [activationStatus, setActivationStatus] = useState(null);
  const [activatingUser, setActivatingUser] = useState(null); // opens single-user activation modal
  const [changingPasswordFor, setChangingPasswordFor] = useState(null); // opens Change Password modal
  // Ship 2: prefill values passed back into the Create/Invite modals when the
  // admin returns from a prepay-new-user Stripe checkout.
  const [modalInitialValues, setModalInitialValues] = useState(null);

  const loadAll = async () => {
    const [t, c, jt, u, inv, jb, act] = await Promise.all([
      techniciansApi.list(),
      customersApi.list(),
      jobTitlesApi.list().catch(() => []),
      isAdmin ? usersApi.list().catch(() => []) : Promise.resolve([]),
      isAdmin ? invitesApi.list().catch(() => []) : Promise.resolve([]),
      jobsApi.list().catch(() => []),
      isAdmin ? billingApi.activationStatus().catch(() => null) : Promise.resolve(null),
    ]);
    setTechs(t);
    setCustomers(c);
    setJobTitles(jt);
    setUsers(u);
    setInvites(inv);
    setJobs(jb || []);
    setActivationStatus(act);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadAll();
        if (!cancelled) setLoading(false);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load staff");
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  // Refresh login accounts + invites + technicians (after create/edit/delete).
  const reloadAccounts = async () => {
    try {
      const [u, inv, t, act] = await Promise.all([
        usersApi.list(),
        invitesApi.list(),
        techniciansApi.list(),
        isAdmin ? billingApi.activationStatus().catch(() => null) : Promise.resolve(null),
      ]);
      setUsers(u);
      setInvites(inv);
      setTechs(t);
      setActivationStatus(act);
    } catch (e) {
      /* non-critical */
    }
  };

  // Reload job titles when a Pay save bubbles back up (so renaming a title elsewhere stays in sync).
  const refreshTitles = async () => {
    try { setJobTitles(await jobTitlesApi.list()); } catch { /* ignore */ }
  };

  // Row-level "Send Invite" — creates a fresh invite (or replaces an
  // already-pending one with resend semantics, handled server-side).
  const sendInvite = async (entry) => {
    const email = entry.kind === "tech" ? entry.tech.email : entry.account?.email;
    if (!email) return;
    const name = entry.kind === "tech" ? entry.tech.name : entry.account?.name;
    try {
      const res = await invitesApi.create({ email, role: entry.permRole, name });
      toast.success(res.email_sent ? `Invite sent to ${email}` : `Invite link created for ${email} — email delivery failed, copy it from Pending Invitations`);
      await reloadAccounts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not send invite");
    }
  };

  // Row-level Deactivate / Reactivate — flips the account's `disabled` flag.
  const toggleDisabled = async (account, disabled) => {
    try {
      await usersApi.update(account.id, { disabled });
      toast.success(disabled ? `${account.name || account.email} deactivated` : `${account.name || account.email} reactivated`);
      await reloadAccounts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not update account");
    }
  };

  const titleNameById = useMemo(() => {
    const m = new Map();
    jobTitles.forEach((t) => m.set(t.id, t.name));
    return m;
  }, [jobTitles]);

  // Resolve the displayed title for a tech: prefer assigned Job Title, fall
  // back to the legacy `role` so techs without a saved title still render.
  const displayTitle = (t) => titleNameById.get(t?.jobTitleId) || t?.role || "";

  // Map email -> login account (for the permission badge + management).
  const userByEmail = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set((u.email || "").toLowerCase(), u));
    return m;
  }, [users]);

  // Ship 1: which login accounts still need activation (post-trial or mixed).
  // Only surface the "Activate" UI when the tenant is actually using the new
  // per-user model — i.e. they're either in a trial or already have at least
  // one activated user. Legacy seat-based tenants continue with the old flow.
  const activationMode = useMemo(() => {
    if (!activationStatus) return "unknown";
    const trialStatus = activationStatus?.trial?.status;
    if (trialStatus === "trialing") return "trial";
    if ((activationStatus?.counts?.activated || 0) > 0) return "migrated";
    if (activationStatus?.monthlyPeriodEnd || activationStatus?.annualPeriodEnd) return "migrated";
    return "legacy";
  }, [activationStatus]);
  const unactivatedUserIds = useMemo(() => {
    const s = new Set();
    if (activationMode === "legacy") return s;
    (activationStatus?.unactivatedUsers || []).forEach((u) => s.add(u.id));
    return s;
  }, [activationStatus, activationMode]);
  const trialEnded = activationMode === "migrated";

  // Return from a per-user activation Stripe checkout — poll and refresh.
  useEffect(() => {
    if (!isAdmin) return;
    const sessionId = searchParams.get("activate_users_session");
    if (!sessionId) return;
    let attempts = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const res = await billingApi.status(sessionId);
        if (res.payment_status === "paid") {
          toast.success("Payment received — user activated!");
          const next = new URLSearchParams(searchParams);
          next.delete("activate_users_session");
          setSearchParams(next, { replace: true });
          await reloadAccounts();
          return;
        }
      } catch (_) { /* keep polling */ }
      attempts += 1;
      if (attempts >= 8) {
        toast.message("Still processing your payment. Refresh in a moment.");
        const next = new URLSearchParams(searchParams);
        next.delete("activate_users_session");
        setSearchParams(next, { replace: true });
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [searchParams, isAdmin, setSearchParams]);

  // Ship 2: return from a prepay-new-user Stripe checkout — pick up the prefill
  // and reopen the correct Create/Invite modal so the admin can finish the flow.
  useEffect(() => {
    if (!isAdmin) return;
    const sessionId = searchParams.get("prepaid_user_session");
    if (!sessionId) return;
    let attempts = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const res = await billingApi.prepayStatus(sessionId);
        if (res.payment_status === "paid") {
          toast.success("Payment received — finish adding your new teammate!");
          const next = new URLSearchParams(searchParams);
          next.delete("prepaid_user_session");
          setSearchParams(next, { replace: true });
          await reloadAccounts();
          setModalInitialValues({
            email: res?.prefill?.email || "",
            name: res?.prefill?.name || "",
            role: res?.prefill?.role || "dispatcher",
          });
          if ((res?.modal_kind || "create") === "invite") setInviteOpen(true);
          else setCreateOpen(true);
          return;
        }
      } catch (_) { /* keep polling */ }
      attempts += 1;
      if (attempts >= 8) {
        toast.message("Still processing your payment. Refresh in a moment.");
        const next = new URLSearchParams(searchParams);
        next.delete("prepaid_user_session");
        setSearchParams(next, { replace: true });
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [searchParams, isAdmin, setSearchParams]);

  const techEmails = useMemo(
    () => new Set(techs.map((t) => (t.email || "").toLowerCase()).filter(Boolean)),
    [techs],
  );

  // Unified roster: every technician card + every login account that has no
  // technician record (admins, dispatchers, office staff).
  const roster = useMemo(() => {
    const techEntries = techs.map((t) => {
      const account = userByEmail.get((t.email || "").toLowerCase()) || null;
      return {
        kind: "tech",
        key: `tech-${t.id}`,
        tech: t,
        account,
        permRole: account?.role || "technician",
        searchText: `${t.name} ${t.role} ${t.phone} ${t.email || ""}`.toLowerCase(),
      };
    });
    const acctEntries = users
      .filter((u) => !techEmails.has((u.email || "").toLowerCase()))
      .map((u) => ({
        kind: "account",
        key: `acct-${u.id}`,
        account: u,
        permRole: u.role,
        searchText: `${u.name || ""} ${u.email} ${u.role}`.toLowerCase(),
      }));
    return [...techEntries, ...acctEntries];
  }, [techs, users, userByEmail, techEmails]);

  // Deep-link: /technicians?id=<techId> auto-opens that tech's drawer once loaded.
  useEffect(() => {
    const id = searchParams.get("id");
    if (!id || loading) return;
    const match = techs.find((t) => t.id === id);
    if (!match) return;
    (async () => {
      await Promise.resolve();
      setSelected(match);
    })();
  }, [searchParams, loading, techs]);

  const filteredRoster = useMemo(() => {
    let list = roster;
    if (roleFilter !== "All") list = list.filter((e) => e.permRole === roleFilter);
    if (statusFilter !== "All") list = list.filter((e) => e.kind === "tech" && e.tech.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e) => e.searchText.includes(q));
    }
    return list;
  }, [roster, search, roleFilter, statusFilter]);

  const stats = useMemo(() => {
    const onJob = techs.filter((t) => t.status === "On Job").length;
    const available = techs.filter((t) => t.status === "Available").length;
    const revToday = techs.reduce((s, t) => s + t.performance.revenueToday, 0);
    const avgRating = techs.length ? techs.reduce((s, t) => s + t.performance.rating, 0) / techs.length : 0;
    return { onJob, available, revToday, avgRating };
  }, [techs]);

  const accountCount = roster.filter((e) => e.kind === "account").length;

  usePageHeader({
    title: "Staff",
    subtitle: `${roster.length} staff · ${techs.length} technicians · ${stats.onJob} on jobs`,
    actions:
      isAdmin && (
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="staff-create-direct-btn"
            className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]"
          >
            <UserPlus className="h-4 w-4 mr-1.5" /> Add Staff
          </Button>
          {hasModule("bulk_csv_invites") && (
            <Button
              variant="outline"
              onClick={() => setBulkOpen(true)}
              data-testid="staff-bulk-csv-btn"
              className="h-9 text-[13px]"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Bulk via CSV
            </Button>
          )}
        </div>
      ),
  });

  return (
    <>
      <div className="p-6 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi icon={HardHat} label="On Roster" value={techs.length} accent="#2563EB" />
          <Kpi icon={Activity} label="On Job Now" value={`${stats.onJob} / ${techs.length}`} accent="#10B981" />
          <Kpi icon={ShieldCheck} label="Other Accounts" value={accountCount} accent="#8B5CF6" />
          <Kpi icon={DollarSign} label="Today's Revenue" value={`$${stats.revToday.toLocaleString()}`} accent="#F59E0B" />
        </div>

        {/* Pending invitations */}
        {isAdmin && invites.length > 0 && (
          <PendingInvites
            invites={invites}
            onRevoke={async (inv) => {
              if (!window.confirm(`Revoke invite for "${inv.email}"?`)) return;
              try {
                await invitesApi.revoke(inv.id);
                setInvites((list) => list.filter((x) => x.id !== inv.id));
                toast.success(`Revoked invite for ${inv.email}`);
              } catch (err) {
                toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Revoke failed");
              }
            }}
            onResend={async (inv) => {
              try {
                const updated = await invitesApi.resend(inv.id);
                setInvites((list) => list.map((x) => (x.id === inv.id ? updated : x)));
                toast.success(
                  updated.email_sent
                    ? `Re-sent invite to ${inv.email}`
                    : `Refreshed invite link (email send failed — copy the link)`,
                );
              } catch (err) {
                toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Resend failed");
              }
            }}
          />
        )}

        {/* Filter row */}
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, role, phone..."
              data-testid="staff-search"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 bg-slate-50 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 focus:bg-white"
            />
          </div>
          <RoleFilter value={roleFilter} onChange={setRoleFilter} />
          <Pills label="Status" options={["All", "On Job", "Available", "En Route", "Lunch"]} value={statusFilter} onChange={setStatusFilter} />
        </div>

        {/* Roster — flat table (line items) instead of colored per-role
            sections; sortable-ish column headers match the Customers page. */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="staff-list">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50/60 text-[10.5px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left font-semibold px-5 py-3">Staff</th>
                  <th className="text-left font-semibold py-3">Contact</th>
                  <th className="text-left font-semibold py-3">Role</th>
                  <th className="text-left font-semibold py-3">Job Title</th>
                  <th className="text-left font-semibold py-3">Status</th>
                  <th className="text-left font-semibold py-3">Last Active</th>
                  <th className="text-right font-semibold px-5 py-3">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoster.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-slate-400 text-[13px]">
                      No staff match these filters.
                    </td>
                  </tr>
                ) : (
                  filteredRoster.map((e) => {
                    const acctId = e.account?.id || null;
                    // A staff member who has already logged in (and isn't
                    // disabled) is active — never show "Activate" for them,
                    // regardless of how they came to be active.
                    const alreadyActive = Boolean(e.account?.last_login_at) && !e.account?.disabled;
                    const needsActivation = isAdmin && acctId && unactivatedUserIds.has(acctId) && !alreadyActive;
                    return (
                      <StaffTableRow
                        key={e.key}
                        entry={e}
                        title={e.kind === "tech" ? displayTitle(e.tech) : ROLE_LABELS[e.permRole]}
                        needsActivation={needsActivation}
                        trialEnded={trialEnded}
                        currentUserId={user?.id}
                        onSendInvite={sendInvite}
                        onChangePassword={setChangingPasswordFor}
                        onToggleDisabled={toggleDisabled}
                        onActivate={
                          needsActivation && e.account
                            ? () => setActivatingUser(e.account)
                            : undefined
                        }
                        onClick={() =>
                          e.kind === "tech" ? setSelected(e.tech) : setEditingAccount(e.account)
                        }
                      />
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && (
        <TechDetail
          tech={selected}
          customers={customers}
          jobs={jobs}
          users={users}
          title={displayTitle(selected)}
          onRemoved={() => { setSelected(null); loadAll(); }}
          onClose={() => {
            setSelected(null);
            reloadAccounts();
            if (searchParams.get("id")) {
              const next = new URLSearchParams(searchParams);
              next.delete("id");
              setSearchParams(next, { replace: true });
            }
          }}
          onUpdate={async (updated, persist = true) => {
            // Optimistic UI; persist to backend (skip for upload flows already saved server-side).
            setTechs((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            setSelected(updated);
            if (!persist) return;
            try {
              // Documents & avatarUrl are managed via dedicated upload endpoints — never
              // overwrite them through the generic PATCH (the model omits internal storage paths).
              const { id, documents, avatarUrl, ...patch } = updated;
              void documents;
              void avatarUrl;
              await techniciansApi.update(id, patch);
            } catch (err) {
              console.error(err);
              toast.error("Failed to save technician changes");
            }
          }}
        />
      )}

      {createOpen && (
        <CreateUserModal
          initialValues={modalInitialValues}
          onClose={() => { setCreateOpen(false); setModalInitialValues(null); }}
          onCreated={(nu) => {
            setCreateOpen(false);
            setModalInitialValues(null);
            reloadAccounts();
            toast.success(`Added ${nu.email}`);
          }}
        />
      )}
      {inviteOpen && (
        <InviteModal
          initialValues={modalInitialValues}
          onClose={() => { setInviteOpen(false); setModalInitialValues(null); }}
          onSent={(inv) => setInvites((list) => [inv, ...list])}
        />
      )}
      {editingAccount && (
        <AccountDetail
          account={editingAccount}
          isAdmin={isAdmin}
          isSelf={user && editingAccount.id === user.id}
          jobTitles={jobTitles}
          customers={customers}
          jobs={jobs}
          onClose={() => { setEditingAccount(null); reloadAccounts(); }}
          onSaved={(updated) => {
            setEditingAccount(null);
            reloadAccounts();
            toast.success(`Updated ${updated.email}`);
          }}
          onDeleted={(acct) => {
            setEditingAccount(null);
            reloadAccounts();
            toast.success(`Deleted ${acct.email}`);
          }}
        />
      )}
      {bulkOpen && (
        <BulkInviteModal
          onClose={() => setBulkOpen(false)}
          onComplete={(res) => {
            const n = res?.summary?.invited || 0;
            if (n > 0) toast.success(`Sent ${n} invitation${n === 1 ? "" : "s"}`);
            reloadAccounts();
          }}
        />
      )}
      {activatingUser && (
        <ActivateOneUserModal
          user={activatingUser}
          status={activationStatus}
          onClose={() => setActivatingUser(null)}
        />
      )}
      {changingPasswordFor && (
        <ChangePasswordModal
          account={changingPasswordFor}
          onClose={() => setChangingPasswordFor(null)}
        />
      )}
    </>
  );
}

function RoleFilter({ value, onChange }) {
  const opts = [
    ["All", "All"],
    ["admin", "Admin"],
    ["dispatcher", "Dispatcher"],
    ["technician", "Tech"],
    ["office_personnel", "Office"],
  ];
  return (
    <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 border border-slate-200" data-testid="staff-role-filter">
      <span className="text-[10.5px] uppercase font-semibold text-slate-400 tracking-wider px-1.5">Role</span>
      {opts.map(([val, label]) => (
        <button
          key={val}
          onClick={() => onChange(val)}
          data-testid={`staff-role-filter-${val}`}
          className={`h-7 px-2.5 rounded-md text-[11.5px] font-medium transition-colors ${
            value === val ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// StaffTableRow — new flat-table renderer that replaced the colored per-role
// StaffListRow cards. Same underlying `entry` shape (tech OR account), rendered
// as one <tr> with the columns: Staff · Contact · Role · Job Title · Status ·
// Last Active · (activate action). Matches the Customers-page pattern so both
// pages feel consistent.
// -----------------------------------------------------------------------------
function StaffTableRow({ entry, title, needsActivation, trialEnded, onActivate, onClick, currentUserId, onSendInvite, onChangePassword, onToggleDisabled }) {
  const isTech = entry.kind === "tech";
  const tech = entry.tech;
  const account = entry.account;
  const name = isTech ? tech.name : account.name || account.email.split("@")[0];
  const email = isTech ? tech.email : account.email;
  const phone = isTech ? tech.phone : "";
  const initials = (name || email || "?").slice(0, 2).toUpperCase();
  const lastActive = account?.last_active_at || account?.last_login_at;
  const status = isTech ? tech.status : null;
  const testid = `staff-row-${isTech ? tech.id : account.id}`;
  return (
    <tr
      onClick={onClick}
      data-testid={testid}
      className="border-b border-slate-100 last:border-0 hover:bg-blue-50/40 cursor-pointer transition-colors"
    >
      {/* Staff (avatar + name + trial pill) */}
      <td className="px-5 py-3">
        <div className="flex items-center gap-2.5">
          {isTech && tech.avatarUrl ? (
            <img src={techniciansApi.avatarUrl(tech)} alt={name} className="h-9 w-9 rounded-full object-cover shrink-0" />
          ) : (
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-slate-500 to-slate-700 text-white text-[11.5px] font-bold flex items-center justify-center shrink-0">
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
              {name}
              {needsActivation && (
                <span
                  data-testid={`staff-row-trial-pill-${isTech ? tech.id : account.id}`}
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border shrink-0 ${
                    trialEnded
                      ? "bg-rose-50 text-rose-700 border-rose-200"
                      : "bg-amber-50 text-amber-700 border-amber-200"
                  }`}
                >
                  {trialEnded ? "Trial ended" : "Trial"}
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {isTech ? "Technician" : ROLE_LABELS[entry.permRole]}
            </div>
          </div>
          <StaffActionsMenu
            entry={entry}
            currentUserId={currentUserId}
            onSendInvite={onSendInvite}
            onChangePassword={onChangePassword}
            onToggleDisabled={onToggleDisabled}
          />
        </div>
      </td>
      {/* Contact */}
      <td className="py-3">
        <div className="text-slate-700 text-[12.5px] truncate max-w-[220px]">{email || "—"}</div>
        {phone && <div className="text-[11px] text-slate-500">{phone}</div>}
      </td>
      {/* Role badge */}
      <td className="py-3">
        <PermBadge role={entry.permRole} testid={`staff-row-role-${isTech ? tech.id : account.id}`} />
      </td>
      {/* Job Title */}
      <td className="py-3">
        <span className="text-[12.5px] text-slate-700">{title || "—"}</span>
      </td>
      {/* Status */}
      <td className="py-3">
        {account?.disabled ? (
          <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-400" data-testid={`staff-row-deactivated-badge-${isTech ? tech.id : account.id}`}>
            <span className="h-2 w-2 rounded-full bg-slate-400" />
            Deactivated
          </span>
        ) : status ? (
          <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-700">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: (statusColorMap[status] || {}).dot || "#94A3B8" }} />
            {status}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11.5px] text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5" /> Account
          </span>
        )}
      </td>
      {/* Last active */}
      <td className="py-3 text-[12px] text-slate-500 whitespace-nowrap">
        {timeAgo(lastActive) || "—"}
      </td>
      {/* Actions (Activate button for trial users) */}
      <td className="px-5 py-3 text-right">
        {needsActivation && onActivate ? (
          <button
            onClick={(ev) => { ev.stopPropagation(); onActivate(); }}
            data-testid={`staff-row-activate-${isTech ? tech.id : account.id}`}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold"
            title="Activate this user"
          >
            <Rocket className="h-3 w-3" /> Activate
          </button>
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-300 ml-auto" />
        )}
      </td>
    </tr>
  );
}

// Row-level 3-dot menu — Deactivate/Reactivate, Change Password, Send Invite.
// Visibility rules:
//  - Send Invite: only shows for staff who have never logged in yet (no
//    account at all = pending onboarding, or an account that was created
//    but never signed in) — i.e. "have not yet completed onboarding".
//  - Deactivate: only for accounts that are currently active (logged in at
//    least once and not already disabled).
//  - Reactivate: the inverse — a previously-active account that's disabled.
//  - Change Password: any staff member with a login account.
//  - Never shown for the logged-in admin's own row.
function StaffActionsMenu({ entry, currentUserId, onSendInvite, onChangePassword, onToggleDisabled }) {
  const account = entry.account;
  const isSelf = Boolean(account && account.id === currentUserId);
  const hasLoggedIn = Boolean(account?.last_login_at);
  const isDisabled = Boolean(account?.disabled);
  const isActive = hasLoggedIn && !isDisabled;
  const email = entry.kind === "tech" ? entry.tech.email : account?.email;

  const canSendInvite = !hasLoggedIn && Boolean(email);
  const canChangePassword = Boolean(account) && !isSelf;
  const canDeactivate = Boolean(account) && isActive && !isSelf;
  const canReactivate = Boolean(account) && isDisabled && !isSelf;

  if (!canSendInvite && !canChangePassword && !canDeactivate && !canReactivate) return null;

  const id = entry.kind === "tech" ? entry.tech.id : account.id;
  const displayName = entry.kind === "tech" ? entry.tech.name : account.name || account.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(ev) => ev.stopPropagation()}
          data-testid={`staff-row-menu-btn-${id}`}
          className="h-6 w-6 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 shrink-0"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        data-testid={`staff-row-menu-${id}`}
        onClick={(ev) => ev.stopPropagation()}
        className="w-48"
      >
        {canSendInvite && (
          <DropdownMenuItem data-testid={`staff-row-send-invite-${id}`} onSelect={() => onSendInvite(entry)}>
            <Send className="h-3.5 w-3.5" /> Send Invite
          </DropdownMenuItem>
        )}
        {canChangePassword && (
          <DropdownMenuItem data-testid={`staff-row-change-password-${id}`} onSelect={() => onChangePassword(account)}>
            <KeyRound className="h-3.5 w-3.5" /> Change Password
          </DropdownMenuItem>
        )}
        {canDeactivate && (
          <DropdownMenuItem
            data-testid={`staff-row-deactivate-${id}`}
            className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
            onSelect={() => {
              if (window.confirm(`Deactivate ${displayName}? They will no longer be able to sign in.`)) {
                onToggleDisabled(account, true);
              }
            }}
          >
            <UserX className="h-3.5 w-3.5" /> Deactivate
          </DropdownMenuItem>
        )}
        {canReactivate && (
          <DropdownMenuItem
            data-testid={`staff-row-reactivate-${id}`}
            className="text-emerald-600 focus:text-emerald-700 focus:bg-emerald-50"
            onSelect={() => onToggleDisabled(account, false)}
          >
            <UserCheck className="h-3.5 w-3.5" /> Reactivate
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Small standalone modal for the row-level "Change Password" action.
function ChangePasswordModal({ account, onClose }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (ev) => {
    ev.preventDefault();
    if (pw.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await usersApi.update(account.id, { password: pw });
      toast.success(`Password changed for ${account.email}`);
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6" data-testid="change-password-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-bold text-slate-900">Change Password</h3>
          <button onClick={onClose} data-testid="change-password-close-btn" className="text-slate-400 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[12.5px] text-slate-500 mb-3">Set a new password for {account.email}.</p>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="New password"
            autoFocus
            data-testid="change-password-input"
            className="w-full h-9 px-3 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          />
          {error && <div className="text-[12px] text-rose-600" data-testid="change-password-error">{error}</div>}
          <Button type="submit" disabled={saving} data-testid="change-password-submit-btn" className="w-full h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">
            {saving ? "Saving…" : "Save Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}



// Side drawer for non-technician staff (Admin / Dispatcher / Office Personnel) —
// mirrors the technician drawer's look, shows the account profile + management.
function AccountDetail({ account, isAdmin, isSelf, jobTitles = [], customers = [], jobs = [], onClose, onSaved, onDeleted }) {
  const initialTypeId = account.permissionTypeId || account.role;
  const [name, setName] = useState(account.name || "");
  const [permTypeId, setPermTypeId] = useState(initialTypeId);
  const [types, setTypes] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Default to Overview so every staff member lands on the same first tab
  // as technicians — the previous fix was reverted at some point.
  const [tab, setTab] = useState("overview");

  // Staff profile (availability / days off / certifications / compensation),
  // persisted on the user document so every staff member gets the same page
  // and their compensation drives the Payroll report.
  const defaultSchedule = () => {
    const s = {};
    DAYS.forEach((d) => {
      const work = d !== "Sat" && d !== "Sun";
      s[d] = { active: work, start: work ? "08:00" : "", end: work ? "17:00" : "" };
    });
    return s;
  };
  const [profile, setProfile] = useState({
    schedule: account.availability || defaultSchedule(),
    daysOff: account.daysOff || [],
    certifications: account.certifications || [],
    pay: account.pay || { model: "hourly", hourlyRate: 0, overtimeRule: "40wk", overtimeMultiplier: 1.5, commissionPct: 0, commissionBase: "job-value", flatRatePerJob: 0, tradeOverrides: [] },
    jobTitleId: account.jobTitleId || "",
    phone: account.phone || "",
    payProfile: account.payProfile || {},
  });
  const [savingPay, setSavingPay] = useState(false);

  const persistProfile = async (patch) => {    try {
      await usersApi.update(account.id, patch);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    }
  };
  const onAvailUpdate = (merged) => {
    setProfile((p) => ({ ...p, schedule: merged.schedule }));
    persistProfile({ availability: merged.schedule });
  };
  const onDaysOffUpdate = (merged) => {
    setProfile((p) => ({ ...p, daysOff: merged.daysOff }));
    persistProfile({ daysOff: merged.daysOff });
  };
  const onCertsChange = (certs) => {
    setProfile((p) => ({ ...p, certifications: certs }));
    persistProfile({ certifications: certs });
  };
  const savePaySettings = async () => {
    setSavingPay(true);
    try {
      await usersApi.update(account.id, { pay: profile.pay, jobTitleId: profile.jobTitleId || null, phone: profile.phone, payProfile: profile.payProfile || {} });
      toast.success("Pay settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSavingPay(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    permissionsApi.types().then((t) => active && setTypes(t)).catch(() => active && setTypes([]));
    return () => { active = false; };
  }, [isAdmin]);

  const selectedType = types.find((t) => t.id === permTypeId);
  const displayName = account.name || account.email.split("@")[0];
  const initials = (account.name || account.email).slice(0, 2).toUpperCase();
  const roleLabel = ROLE_LABELS[account.role] || account.role;
  const isActive = Boolean(account.last_login_at);
  const theme = ROLE_THEME[account.role] || ROLE_THEME.office_personnel;

  // Synthesize a `tech`-like object for Overview + Performance so every staff
  // member (admin / dispatcher / CSR / office personnel) gets the same two
  // tabs technicians do. Non-tech roles have no GPS pings and no jobs so the
  // OverviewTab & PerformanceTab render empty-state placeholders — but the
  // tabs are ALWAYS present as the user requires.
  const accountTech = useMemo(() => {
    // Jobs assigned to this user (unusual for non-techs but we support it).
    const weekJobs = (jobs || [])
      .filter((j) => j.techId === account.id || j.assignedTo === account.id)
      .map((j) => ({
        id: j.id,
        date: j.start || j.scheduledFor || j.createdAt,
        customer: j.customer || j.customerName || "",
        type: j.type || j.jobType || "",
        status: j.status || "Scheduled",
      }));

    const todayKey = new Date().toISOString().slice(0, 10);
    const weekStart = (() => {
      const d = new Date();
      d.setDate(d.getDate() - d.getDay());
      return d.toISOString().slice(0, 10);
    })();
    const monthKey = new Date().toISOString().slice(0, 7);

    const filterByDate = (predicate) => weekJobs.filter((j) => predicate((j.date || "").slice(0, 10)));
    const jobsToday = filterByDate((d) => d === todayKey);
    const jobsWeek = filterByDate((d) => d >= weekStart);
    const jobsMonth = filterByDate((d) => d.slice(0, 7) === monthKey);
    const sumValue = (arr) => arr.reduce((s, j) => s + (j.value || 0), 0);

    return {
      id: account.id,
      name: displayName,
      color: theme.accent || "#64748b",
      avatar: initials,
      status: isActive ? "Available" : "Offline",
      location: null,      // no GPS ping for non-techs
      weekJobs,
      performance: {
        jobsToday: jobsToday.length,
        jobsWeek: jobsWeek.length,
        jobsMonth: jobsMonth.length,
        revenueToday: sumValue(jobsToday),
        revenueWeek: sumValue(jobsWeek),
        revenueMonth: sumValue(jobsMonth),
        avgTicket: jobsMonth.length ? Math.round(sumValue(jobsMonth) / jobsMonth.length) : 0,
        conversionRate: 0,
        rating: 0,
        reviewCount: 0,
      },
    };
  }, [account.id, jobs, displayName, initials, isActive, theme.accent]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (isSelf && selectedType && selectedType.baseRole !== "admin") {
      setError("You can't remove your own admin access — it would lock you out.");
      return;
    }
    const patch = {};
    if (name.trim() && name !== account.name) patch.name = name.trim();
    if (permTypeId !== initialTypeId) patch.permissionTypeId = permTypeId || null;
    if (newPassword) patch.password = newPassword;
    if (Object.keys(patch).length === 0) { onClose(); return; }
    setSubmitting(true);
    try {
      const updated = await usersApi.update(account.id, patch);
      onSaved && onSaved(updated);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const [confirmRemove, setConfirmRemove] = useState(false);
  const doRemove = async () => {
    try {
      await usersApi.remove(account.id);
      setConfirmRemove(false);
      onDeleted && onDeleted(account);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      setConfirmRemove(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose} data-testid="account-detail-drawer">
      <div className="absolute inset-0 bg-slate-900/40" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-[560px] max-w-[95vw] h-full bg-white shadow-2xl border-l border-slate-200 overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 text-white text-[16px] font-bold flex items-center justify-center shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="text-[18px] font-bold text-slate-900 truncate" data-testid="account-detail-name">{displayName}</h2>
                {isActive && (
                  <span data-testid="account-active-badge" className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 shrink-0">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
                  </span>
                )}
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <PermBadge role={account.role} testid={`account-detail-role-${account.id}`} />
                <span className="text-[10.5px] text-slate-500">Last active {timeAgo(account.last_active_at || account.last_login_at)}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="account-detail-close" className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center shrink-0">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {/* Contact */}
        <div className="px-6 py-3 border-b border-slate-100 text-[12.5px]">
          <a href={`mailto:${account.email}`} className="flex items-center gap-2 text-blue-600 hover:underline truncate">
            <Mail className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{account.email}</span>
          </a>
        </div>

        {isAdmin ? (
          <>
            {/* Tabs */}
            <div className="px-6 border-b border-slate-100 flex items-center gap-0.5 overflow-x-auto" data-testid="account-tabs">
              {[
                { k: "overview", label: "Overview", icon: Activity },
                { k: "performance", label: "Performance", icon: TrendingUp },
                { k: "availability", label: "Availability", icon: CalendarDays },
                { k: "daysoff", label: "Days Off", icon: CalendarOff, count: profile.daysOff.length },
                { k: "certs", label: "Certifications", icon: Award, count: profile.certifications.length },
                { k: "profile", label: "Profile", icon: User },
              ].map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.k}
                    onClick={() => setTab(t.k)}
                    data-testid={`account-tab-${t.k}`}
                    className={`flex items-center gap-1.5 px-3 py-2.5 text-[12px] font-medium border-b-2 transition-colors whitespace-nowrap ${
                      tab === t.k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {t.label}
                    {typeof t.count === "number" && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === t.k ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>
                        {t.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {tab === "overview" && (
                <OverviewTab tech={accountTech} customers={customers} />
              )}
              {tab === "performance" && (
                <PerformanceTab tech={accountTech} />
              )}
              {tab === "availability" && (
                <AvailabilityTab tech={{ schedule: profile.schedule }} onUpdate={onAvailUpdate} />
              )}
              {tab === "daysoff" && (
                <DaysOffTab tech={{ daysOff: profile.daysOff }} onUpdate={onDaysOffUpdate} />
              )}
              {tab === "certs" && (
                <AccountCertsEditor certs={profile.certifications} onChange={onCertsChange} />
              )}
              {tab === "profile" && (
                <div className="space-y-4" data-testid="account-profile-tab">
                  <div className={`rounded-xl border ${theme.border} ${theme.bg} px-4 py-3`}>
                    <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Account</div>
                    <div className="grid grid-cols-2 gap-3 text-[12.5px]">
                      <div>
                        <div className="text-slate-400">User Type</div>
                        <div className="font-semibold text-slate-800">{roleLabel}</div>
                      </div>
                      <div>
                        <div className="text-slate-400">Status</div>
                        <div className="font-semibold text-slate-800">{isActive ? "Active" : "Never signed in"}</div>
                      </div>
                    </div>
                  </div>

                  <StaffCompEditor
                    profile={profile}
                    setProfile={setProfile}
                    jobTitles={jobTitles}
                    onSave={savePaySettings}
                    saving={savingPay}
                    employeeId={account.id}
                  />

                  <form onSubmit={submit} className="space-y-3.5 border-t border-slate-100 pt-4" data-testid="account-detail-form">
                    <div className="text-[12px] font-bold uppercase tracking-wide text-slate-400">Manage account</div>
                    <div>
                      <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Name</label>
                      <input value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                    </div>
                    <div>
                      <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Permission Type</label>
                      <select
                        value={permTypeId}
                        onChange={(e) => setPermTypeId(e.target.value)}
                        data-testid="account-permission-type"
                        className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                      >
                        {types.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}{t.system ? "" : ` (custom · ${t.baseRole.replace("_", " ")})`}
                          </option>
                        ))}
                      </select>
                      <div className="text-[10.5px] text-slate-400 mt-1">Controls which areas this user can access. Manage the list under Company Settings → Permission Types.</div>
                      {isSelf && (
                        <div className="text-[10.5px] text-amber-600 mt-1">You can&apos;t remove your own admin access — would lock you out of admin tools.</div>
                      )}
                    </div>
                    <div>
                      <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">New password (optional)</label>
                      <input type="password" minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="account-password" placeholder="Leave blank to keep current" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                    </div>
                    {error && <div data-testid="account-error" className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">{error}</div>}
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <Button type="button" variant="outline" disabled={isSelf} onClick={() => setConfirmRemove(true)} data-testid="account-delete" className="h-9 text-[13px] text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300 disabled:opacity-40">
                        <Trash2 className="h-4 w-4 mr-1.5" /> Remove staff member
                      </Button>
                      <Button type="submit" disabled={submitting} data-testid="account-submit" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px]">
                        {submitting ? "Saving…" : "Save changes"}
                      </Button>
                    </div>
                  </form>

                  {/* Dedicated height-based spacer (not padding — theme CSS overrides
                      padding) so the Save button clears the fixed mobile bottom nav,
                      plus two full extra rows (4rem each) of breathing room below it. */}
                  <div aria-hidden="true" className="md:hidden shrink-0" style={{ height: "calc(5rem + 8rem + env(safe-area-inset-bottom))" }} />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className={`rounded-xl border ${theme.border} ${theme.bg} px-4 py-3`}>
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Account</div>
              <div className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div>
                  <div className="text-slate-400">User Type</div>
                  <div className="font-semibold text-slate-800">{roleLabel}</div>
                </div>
                <div>
                  <div className="text-slate-400">Status</div>
                  <div className="font-semibold text-slate-800">{isActive ? "Active" : "Never signed in"}</div>
                </div>
              </div>
            </div>
            <div className="text-[12.5px] text-slate-500">Only admins can manage account settings.</div>
          </div>
        )}
      </div>
      <ConfirmRemoveModal
        open={confirmRemove}
        name={account.name || account.email}
        onConfirm={doRemove}
        onClose={() => setConfirmRemove(false)}
      />
    </div>
  );
}



function AccountCard({ account, onClick }) {
  const initials = (account.name || account.email).slice(0, 2).toUpperCase();
  return (
    <div
      onClick={onClick}
      data-testid={`staff-account-card-${account.id}`}
      className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:-translate-y-0.5 hover:border-blue-300 cursor-pointer transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-slate-500 to-slate-700 text-white text-[14px] font-bold flex items-center justify-center shrink-0">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[14px] font-semibold text-slate-900 truncate">
                {account.name || account.email.split("@")[0]}
              </div>
              <div className="text-[11.5px] text-slate-500 truncate">{account.email}</div>
            </div>
            <PermBadge role={account.role} testid={`staff-account-role-${account.id}`} />
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5" /> Login account
            </span>
            <span className="text-blue-600 font-medium">Manage →</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PendingInvites({ invites, onRevoke, onResend }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="invites-table-wrap">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[14px] font-semibold text-slate-900">
          <Clock className="h-4 w-4 text-amber-500" /> Pending invitations
          <span className="text-[11.5px] text-slate-500 font-normal">— expire in 72 hours</span>
        </div>
        <div className="text-[11.5px] text-slate-500">{invites.length} pending</div>
      </div>
      <table className="w-full text-[13px]" data-testid="invites-table">
        <thead className="bg-slate-50/60 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-5 py-2.5 font-semibold">Recipient</th>
            <th className="text-left px-3 py-2.5 font-semibold">Role</th>
            <th className="text-left px-3 py-2.5 font-semibold">Status</th>
            <th className="text-left px-3 py-2.5 font-semibold">Magic link</th>
            <th className="text-right px-5 py-2.5 font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {invites.map((inv) => (
            <tr key={inv.id} data-testid={`invite-row-${inv.id}`}>
              <td className="px-5 py-3">
                <div className="font-semibold text-slate-800">{inv.email}</div>
                <div className="text-[11.5px] text-slate-500">
                  Invited by {inv.invited_by} · {(inv.created_at || "").slice(0, 10)}
                </div>
              </td>
              <td className="px-3 py-3">
                <PermBadge role={inv.role} />
              </td>
              <td className="px-3 py-3">
                {inv.email_sent ? (
                  <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Email sent
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-amber-700">
                    <AlertCircle className="h-3.5 w-3.5" /> Email pending · copy link
                  </span>
                )}
              </td>
              <td className="px-3 py-3">
                <CopyableLink url={inv.accept_url} testid={`invite-copy-${inv.id}`} />
              </td>
              <td className="px-5 py-3 text-right space-x-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onResend(inv)}
                  data-testid={`invite-resend-${inv.id}`}
                  className="h-7 text-[11.5px]"
                >
                  <RefreshCw className="h-3 w-3 mr-1" /> Resend
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRevoke(inv)}
                  data-testid={`invite-revoke-${inv.id}`}
                  className="h-7 text-[11.5px] text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300"
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Revoke
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{label}</div>
          <div className="mt-1.5 text-[24px] font-bold text-slate-900 leading-none">{value}</div>
        </div>
        <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: `${accent}18` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
      </div>
    </div>
  );
}

function Pills({ label, options, value, onChange }) {
  return (
    <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 border border-slate-200">
      <span className="text-[10.5px] uppercase font-semibold text-slate-400 tracking-wider px-1.5">{label}</span>
      {options.slice(0, 5).map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          className={`h-7 px-2.5 rounded-md text-[11.5px] font-medium transition-colors ${
            value === o ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function TechCard({ tech, title, permRole, hasAccount, onClick }) {
  const sc = statusColorMap[tech.status] || statusColorMap.Off;
  const expiringCerts = tech.certifications.filter((c) => c.status === "Expiring Soon" || c.status === "Expired");
  return (
    <div
      onClick={onClick}
      data-testid={`staff-tech-card-${tech.id}`}
      className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:-translate-y-0.5 hover:border-blue-300 cursor-pointer transition-all"
    >
      <div className="flex items-start gap-3">
        <div
          className="h-12 w-12 rounded-full text-white text-[14px] font-bold flex items-center justify-center shrink-0"
          style={{ background: tech.color }}
        >
          {tech.avatar}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[14px] font-semibold text-slate-900 truncate">{tech.name}</div>
              <div className="text-[11.5px] text-slate-500 truncate">{title}</div>
            </div>
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
              style={{ background: sc.bg, color: sc.text }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: sc.dot }} />
              {tech.status}
            </span>
          </div>

          <div className="mt-1.5 flex items-center gap-1.5">
            {permRole && <PermBadge role={permRole} testid={`staff-tech-role-${tech.id}`} />}
            {!hasAccount && (
              <span className="text-[10px] text-slate-400 italic">no login</span>
            )}
          </div>

          <div className="mt-2 grid grid-cols-3 gap-2">
            <Mini label="Today" value={`${tech.performance.jobsToday}j`} sub={`$${tech.performance.revenueToday.toLocaleString()}`} />
            <Mini label="Week" value={`${tech.performance.jobsWeek}j`} sub={`$${(tech.performance.revenueWeek / 1000).toFixed(1)}K`} />
            <Mini label="Rating" value={`★ ${tech.performance.rating}`} sub={`${tech.performance.reviewCount} rev`} />
          </div>

          <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
            <span className="flex items-center gap-1 truncate">
              <Truck className="h-3 w-3 shrink-0" />
              <span className="truncate">{tech.vehicle.split("\u2014")[0].trim()}</span>
            </span>
            {expiringCerts.length > 0 && (
              <span className="flex items-center gap-0.5 text-amber-700 font-semibold">
                <AlertTriangle className="h-3 w-3" />
                {expiringCerts.length} cert
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, sub }) {
  return (
    <div className="bg-slate-50/60 rounded p-1.5 border border-slate-100">
      <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="text-[12.5px] font-bold text-slate-900 leading-tight">{value}</div>
      <div className="text-[10px] text-slate-500 truncate">{sub}</div>
    </div>
  );
}

function TechDetail({ tech, customers, jobs = [], users = [], title, onClose, onUpdate, onRemoved }) {
  const role = title || tech.role;
  const [tab, setTab] = useState("overview");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const sc = statusColorMap[tech.status] || statusColorMap.Off;

  // Real, live schedule for this technician derived from actual jobs assigned
  // to them (techId === tech.id), replacing the static seeded `weekJobs`.
  const realWeekJobs = useMemo(
    () =>
      (jobs || [])
        .filter((j) => j.techId === tech.id && !["Completed", "Canceled"].includes(j.status))
        .map((j) => ({ id: j.id, status: j.status, customer: j.customer, date: j.start, type: j.type }))
        .sort((a, b) => (a.date || "").localeCompare(b.date || "")),
    [jobs, tech.id],
  );
  const techView = useMemo(() => ({ ...tech, weekJobs: realWeekJobs }), [tech, realWeekJobs]);
  // A staff member is "Active" once their linked login account has signed in
  // at least once (covers both accepted invites and direct-created accounts).
  const linkedLogin = (users || []).find(
    (u) => (u.email || "").toLowerCase() === (tech.email || "").toLowerCase()
  );
  const isActive = Boolean(tech.email && linkedLogin?.last_login_at);

  const removeStaff = async () => {
    try {
      if (linkedLogin?.id) {
        await usersApi.remove(linkedLogin.id);
      } else {
        // No linked login — remove just the roster card.
        await techniciansApi.remove(tech.id);
      }
      setConfirmRemove(false);
      toast.success(`Removed ${tech.name}`);
      onRemoved ? onRemoved() : onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to remove staff member");
      setConfirmRemove(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose} data-testid="tech-detail-drawer">
      <div className="absolute inset-0 bg-slate-900/40" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-[680px] max-w-[95vw] h-full bg-white shadow-2xl border-l border-slate-200 overflow-hidden flex flex-col"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className="h-14 w-14 rounded-xl text-white text-[16px] font-bold flex items-center justify-center shrink-0 overflow-hidden"
              style={{ background: tech.color }}
            >
              {tech.avatarUrl ? (
                <img
                  src={techniciansApi.avatarUrl(tech)}
                  alt={tech.name}
                  data-testid="staff-avatar-img"
                  className="h-full w-full object-cover"
                />
              ) : (
                tech.avatar
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="text-[18px] font-bold text-slate-900 truncate">{tech.name}</h2>
                {isActive && (
                  <span
                    data-testid="staff-active-badge"
                    className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 shrink-0"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Active
                  </span>
                )}
              </div>
              <div className="text-[12.5px] text-slate-500 truncate">{title}</div>
              <div className="mt-1.5 flex items-center gap-1.5">
                <span
                  className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: sc.bg, color: sc.text }}
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: sc.dot }} />
                  {tech.status}
                </span>
                <span className="text-[10.5px] text-slate-500">
                  Hired {new Date(tech.hireDate).toLocaleDateString([], { month: "short", year: "numeric" })}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => setConfirmRemove(true)} data-testid="tech-remove-btn" title="Remove staff member" className="h-8 w-8 rounded-md hover:bg-red-50 flex items-center justify-center">
              <Trash2 className="h-4 w-4 text-red-500" />
            </button>
            <button onClick={onClose} className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center">
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-4 border-b border-slate-100 bg-slate-50/40">
          <Stat label="Today" value={`${tech.performance.jobsToday} jobs`} accent="text-slate-900" />
          <Stat label="Revenue MTD" value={`$${(tech.performance.revenueMonth / 1000).toFixed(1)}K`} accent="text-emerald-700" />
          <Stat label="Avg. Ticket" value={`$${tech.performance.avgTicket.toLocaleString()}`} accent="text-slate-900" />
          <Stat label="Rating" value={`★ ${tech.performance.rating} (${tech.performance.reviewCount})`} accent="text-amber-500" />
        </div>

        {/* Contact row */}
        <div className="px-6 py-3 border-b border-slate-100 grid grid-cols-2 gap-2 text-[12.5px]">
          <a href={`tel:${tech.phone.replace(/[^0-9]/g, "")}`} className="flex items-center gap-2 text-blue-600 hover:underline">
            <Phone className="h-3.5 w-3.5" /> {tech.phone}
          </a>
          <a href={`mailto:${tech.email}`} className="flex items-center gap-2 text-blue-600 hover:underline truncate">
            <Mail className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{tech.email}</span>
          </a>
          <div className="col-span-2 flex items-center gap-2 text-slate-700">
            <Truck className="h-3.5 w-3.5 text-slate-400" /> {tech.vehicle}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-slate-100 flex items-center gap-0.5 overflow-x-auto">
          {[
            { k: "payroll", label: "Profile", icon: User },
            { k: "overview", label: "Overview", icon: Activity },
            { k: "performance", label: "Performance", icon: TrendingUp },
            { k: "schedule", label: "Schedule", icon: Briefcase, count: realWeekJobs.length },
            { k: "availability", label: "Availability", icon: CalendarDays },
            { k: "daysoff", label: "Days Off", icon: CalendarOff, count: tech.daysOff.length },
            { k: "certs", label: "Certifications", icon: Award, count: tech.certifications.length },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-[12px] font-medium border-b-2 transition-colors whitespace-nowrap ${
                  tab === t.k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                {typeof t.count === "number" && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === t.k ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {tab === "overview" && <OverviewTab tech={techView} customers={customers} />}
          {tab === "performance" && <PerformanceTab tech={tech} />}
          {tab === "schedule" && <ScheduleTab tech={techView} customers={customers} />}
          {tab === "availability" && <AvailabilityTab tech={tech} onUpdate={onUpdate} />}
          {tab === "daysoff" && <DaysOffTab tech={tech} onUpdate={onUpdate} />}
          {tab === "certs" && <CertsTab tech={tech} onUpdate={onUpdate} />}
          {tab === "payroll" && <PayrollTab tech={tech} onUpdate={onUpdate} />}
        </div>
      </div>
      <ConfirmRemoveModal
        open={confirmRemove}
        name={tech.name}
        onConfirm={removeStaff}
        onClose={() => setConfirmRemove(false)}
      />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="px-5 py-3 border-r border-slate-100 last:border-r-0">
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`mt-0.5 text-[15px] font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function OverviewTab({ tech, customers }) {
  // Non-technician staff (admin / dispatcher / CSR) don't get GPS pings.
  // Render a friendly placeholder in that slot instead of crashing.
  const hasLocation = tech && tech.location && typeof tech.location.lat === "number";
  const pingDate = hasLocation ? new Date(tech.location.lastPing) : null;
  const center = hasLocation ? [tech.location.lat, tech.location.lng] : null;
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
          <MapPin className="h-3 w-3" /> Live Location
          {hasLocation && (
            <span className="ml-auto text-slate-400 normal-case font-normal">
              Last ping: {pingDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </span>
          )}
        </div>
        {hasLocation ? (
          <>
            <div className="rounded-md overflow-hidden border border-slate-200" style={{ height: 220 }}>
              <MapContainer center={center} zoom={14} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                <TileLayer
                  attribution="&copy; OpenStreetMap"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={center} icon={divIcon(tech.color, tech.avatar)}>
                  <Popup>
                    <strong>{tech.name}</strong>
                    <br />
                    {tech.status} · {pingDate.toLocaleString()}
                  </Popup>
                </Marker>
                <CircleMarker center={center} radius={32} pathOptions={{ color: tech.color, fillColor: tech.color, fillOpacity: 0.12, weight: 1 }} />
              </MapContainer>
            </div>
            <div className="mt-1.5 text-[11px] text-slate-500 font-mono">
              {tech.location.lat.toFixed(4)}, {tech.location.lng.toFixed(4)}
            </div>
          </>
        ) : (
          <div
            className="rounded-md border border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-center px-4"
            style={{ height: 160 }}
            data-testid="overview-no-location"
          >
            <MapPin className="h-6 w-6 text-slate-300 mb-1.5" />
            <div className="text-[12.5px] font-semibold text-slate-600">No live GPS for this role</div>
            <div className="text-[11px] text-slate-500 mt-0.5 max-w-[280px]">
              Office & admin roles don&apos;t send location pings. Assign them to jobs to see their schedule below.
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Today&apos;s Schedule</div>
        {(() => {
          const todayKey = new Date().toISOString().slice(0, 10);
          const todays = (tech.weekJobs || []).filter((j) => (j.date || "").slice(0, 10) === todayKey);
          return todays.length === 0 ? (
            <Empty text="No jobs assigned today." />
          ) : (
            <div className="space-y-1.5">
              {todays.map((j) => (
                <JobRow key={j.id} job={j} customers={customers} />
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function PerformanceTab({ tech }) {
  const p = tech.performance;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <PCard label="Jobs Today" value={p.jobsToday} accent="#2563EB" icon={Briefcase} />
        <PCard label="Jobs This Week" value={p.jobsWeek} accent="#10B981" icon={Briefcase} />
        <PCard label="Jobs This Month" value={p.jobsMonth} accent="#8B5CF6" icon={Briefcase} />
        <PCard label="Revenue Today" value={`$${p.revenueToday.toLocaleString()}`} accent="#2563EB" icon={DollarSign} />
        <PCard label="Revenue This Week" value={`$${(p.revenueWeek / 1000).toFixed(1)}K`} accent="#10B981" icon={DollarSign} />
        <PCard label="Revenue This Month" value={`$${(p.revenueMonth / 1000).toFixed(1)}K`} accent="#8B5CF6" icon={DollarSign} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <PCard label="Avg. Ticket" value={`$${p.avgTicket.toLocaleString()}`} accent="#F59E0B" icon={TrendingUp} />
        <PCard label="Conversion" value={`${p.conversionRate.toFixed(1)}%`} accent="#06B6D4" icon={TrendingUp} />
        <PCard label="Customer Rating" value={`★ ${p.rating} (${p.reviewCount})`} accent="#EAB308" icon={Star} />
      </div>
    </div>
  );
}

function PCard({ label, value, accent, icon: Icon }) {
  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        <Icon className="h-3.5 w-3.5" style={{ color: accent }} />
      </div>
      <div className="mt-1 text-[18px] font-bold text-slate-900">{value}</div>
    </div>
  );
}

function ScheduleTab({ tech, customers }) {
  if (tech.weekJobs.length === 0) return <Empty text="No jobs scheduled this week." />;
  return (
    <div className="space-y-1.5">
      {tech.weekJobs.map((j) => (
        <JobRow key={j.id} job={j} customers={customers} />
      ))}
    </div>
  );
}

function JobRow({ job, customers }) {
  const [expanded, setExpanded] = useState(false);
  const colors = {
    "Scheduled": { bg: "#DBEAFE", text: "#1E40AF" },
    "In Progress": { bg: "#DCFCE7", text: "#166534" },
    "Dispatched": { bg: "#FEF3C7", text: "#92400E" },
    "Completed": { bg: "#E5E7EB", text: "#374151" },
  };
  const sc = colors[job.status] || colors.Scheduled;
  const customer = (customers || []).find((c) => c.name === job.customer);

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden hover:border-blue-300 transition-all">
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[12.5px] font-semibold text-slate-900 flex items-center gap-1.5">
              {job.id}
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: sc.bg, color: sc.text }}>
                {job.status}
              </span>
            </div>
            <button
              onClick={() => setExpanded((v) => !v)}
              disabled={!customer}
              className={`text-[11.5px] truncate text-left ${customer ? "text-blue-600 hover:text-blue-700 hover:underline cursor-pointer" : "text-slate-700"}`}
              title={customer ? "Click to preview customer" : ""}
            >
              {job.customer}
            </button>
            <div className="text-[10.5px] text-slate-500">
              {new Date(job.date).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })} · {job.type}
            </div>
          </div>
        </div>
      </div>

      {expanded && customer && (
        <div className="border-t border-slate-100 bg-blue-50/40 px-3 py-2.5 relative">
          <button
            onClick={() => setExpanded(false)}
            className="absolute top-2 right-2 h-6 w-6 rounded-md hover:bg-white text-slate-500 hover:text-slate-700 flex items-center justify-center transition-colors"
            title="Close preview"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="flex items-start gap-2.5 pr-7">
            <div className={`h-9 w-9 rounded-lg ${customer.type === "Commercial" ? "bg-violet-100" : "bg-blue-100"} flex items-center justify-center shrink-0`}>
              {customer.type === "Commercial" ? (
                <Building2 className="h-4 w-4 text-violet-600" />
              ) : (
                <Home className="h-4 w-4 text-blue-600" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <div className="text-[13px] font-semibold text-slate-900 truncate">{customer.name}</div>
                {customer.tags.includes("VIP") && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />}
              </div>
              <div className="text-[11px] text-slate-500">
                {customer.type} · Customer since {new Date(customer.since).getFullYear()}
              </div>

              <div className="mt-1.5 grid grid-cols-1 gap-y-0.5 text-[11.5px]">
                <a href={`tel:${customer.phone.replace(/[^0-9]/g, "")}`} className="flex items-center gap-1.5 text-blue-600 hover:underline">
                  <Phone className="h-3 w-3" /> {customer.phone}
                </a>
                <a href={`mailto:${customer.email}`} className="flex items-center gap-1.5 text-blue-600 hover:underline truncate">
                  <Mail className="h-3 w-3 shrink-0" /> <span className="truncate">{customer.email}</span>
                </a>
                <div className="flex items-start gap-1.5 text-slate-700">
                  <MapPin className="h-3 w-3 text-slate-400 mt-0.5 shrink-0" />
                  <span>{customer.address}, {customer.city}, {customer.state} {customer.zip}</span>
                </div>
              </div>

              <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                <div className="bg-white rounded border border-slate-200 px-1.5 py-1">
                  <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">LTV</div>
                  <div className="text-[11.5px] font-bold text-emerald-700">${customer.lifetimeValue.toLocaleString()}</div>
                </div>
                <div className="bg-white rounded border border-slate-200 px-1.5 py-1">
                  <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Jobs</div>
                  <div className="text-[11.5px] font-bold text-slate-900">{customer.jobsCount}</div>
                </div>
                <div className="bg-white rounded border border-slate-200 px-1.5 py-1">
                  <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Balance</div>
                  <div className={`text-[11.5px] font-bold ${customer.openBalance > 0 ? "text-amber-700" : "text-slate-700"}`}>
                    ${customer.openBalance.toLocaleString()}
                  </div>
                </div>
              </div>

              {customer.tags.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {customer.tags.map((t) => (
                    <span key={t} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-200/80 text-slate-700">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Time-off balances editor — "Available" is admin-managed; "Used"/"Remaining"
// are read-only, computed server-side from lifetime PTO/SICK adjustment hours.
function TimeOffBalances({ employeeId, pay, updatePay, disabled, idPrefix }) {
  const [balances, setBalances] = useState(null);
  const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));

  useEffect(() => {
    let cancelled = false;
    if (!employeeId) return;
    payrollApi.getBalances(employeeId).then((b) => { if (!cancelled) setBalances(b); }).catch(() => { if (!cancelled) setBalances(null); });
    return () => { cancelled = true; };
  }, [employeeId]);

  return (
    <div className="space-y-2" data-testid={`${idPrefix}-time-off-balances`}>
      <div className="text-[12px] font-bold uppercase tracking-wide text-slate-400">Time off balances</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Lbl>PTO available (hrs)</Lbl>
          <input type="number" min="0" step="0.5" value={pay.ptoBalanceHours ?? 0} onChange={(e) => updatePay({ ptoBalanceHours: num(e.target.value) })} disabled={disabled} data-testid={`${idPrefix}-pto-balance`} className={inpClass} />
          {balances && (
            <div className="text-[11px] text-slate-500 mt-1" data-testid={`${idPrefix}-pto-used-remaining`}>
              {balances.ptoUsedHours.toFixed(2)} used · <span className={balances.ptoRemainingHours < 0 ? "text-rose-600 font-semibold" : "text-emerald-600 font-semibold"}>{balances.ptoRemainingHours.toFixed(2)}</span> remaining
            </div>
          )}
        </div>
        <div>
          <Lbl>Sick available (hrs)</Lbl>
          <input type="number" min="0" step="0.5" value={pay.sickBalanceHours ?? 0} onChange={(e) => updatePay({ sickBalanceHours: num(e.target.value) })} disabled={disabled} data-testid={`${idPrefix}-sick-balance`} className={inpClass} />
          {balances && (
            <div className="text-[11px] text-slate-500 mt-1" data-testid={`${idPrefix}-sick-used-remaining`}>
              {balances.sickUsedHours.toFixed(2)} used · <span className={balances.sickRemainingHours < 0 ? "text-rose-600 font-semibold" : "text-emerald-600 font-semibold"}>{balances.sickRemainingHours.toFixed(2)}</span> remaining
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Compensation + job-title editor for staff accounts (drives the Payroll report).
function StaffCompEditor({ profile, setProfile, jobTitles = [], onSave, saving, employeeId }) {
  const pay = profile.pay || {};
  const showHourly = pay.model === "hourly" || pay.model === "hybrid";
  const showCommission = pay.model === "commission" || pay.model === "hybrid";
  const showSalary = pay.model === "salary";
  const updatePay = (patch) => setProfile((p) => ({ ...p, pay: { ...p.pay, ...patch } }));
  const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));

  return (
    <div className="space-y-3" data-testid="staff-comp-editor">
      <div className="text-[12px] font-bold uppercase tracking-wide text-slate-400">Compensation & job</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Lbl>Job title</Lbl>
          <select
            value={profile.jobTitleId || ""}
            onChange={(e) => setProfile((p) => ({ ...p, jobTitleId: e.target.value }))}
            data-testid="staff-job-title"
            className={selClass}
          >
            <option value="">— None —</option>
            {jobTitles.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Lbl>Phone</Lbl>
          <input
            value={profile.phone || ""}
            onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
            data-testid="staff-phone"
            placeholder="(555) 555-5555"
            className={inpClass}
          />
        </div>
      </div>

      <div>
        <Lbl>Compensation model</Lbl>
        <select
          value={pay.model || "hourly"}
          onChange={(e) => updatePay({ model: e.target.value })}
          data-testid="staff-pay-model"
          className={selClass}
        >
          <option value="hourly">Hourly only</option>
          <option value="commission">Commission only</option>
          <option value="hybrid">Hybrid (hourly + commission)</option>
          <option value="flat">Per-job flat rate</option>
          <option value="piece_rate">Piece rate (per unit)</option>
          <option value="salary">Salary</option>
        </select>
      </div>

      {pay.model === "piece_rate" && (
        <div className="text-[11.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          Piece-rate pay is calculated automatically from completed jobs' invoice line items that use a Pricebook item with a per-unit rate set. Set rates on the <span className="font-semibold">Pricebook</span> page.
        </div>
      )}

      {showSalary && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Lbl>Annual salary ($)</Lbl>
            <input type="number" min="0" step="100" value={pay.annualSalary ?? 0} onChange={(e) => updatePay({ annualSalary: num(e.target.value) })} data-testid="staff-annual-salary" className={inpClass} />
          </div>
          <div>
            <Lbl>Pay frequency</Lbl>
            <select value={pay.payFrequency || "biweekly"} onChange={(e) => updatePay({ payFrequency: e.target.value })} data-testid="staff-pay-frequency" className={selClass}>
              {PAY_FREQUENCIES.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
        </div>
      )}

      {showHourly && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Lbl>Base hourly ($)</Lbl>
            <input type="number" min="0" step="0.5" value={pay.hourlyRate ?? 0} onChange={(e) => updatePay({ hourlyRate: num(e.target.value) })} data-testid="staff-hourly-rate" className={inpClass} />
          </div>
          <div>
            <Lbl>Overtime rule</Lbl>
            <select value={pay.overtimeRule || "40wk"} onChange={(e) => updatePay({ overtimeRule: e.target.value })} data-testid="staff-ot-rule" className={selClass}>
              <option value="40wk">1.5× over 40 hrs/wk</option>
              <option value="ca-daily">CA daily OT</option>
              <option value="none">No overtime</option>
            </select>
          </div>
          <div>
            <Lbl>OT multiplier</Lbl>
            <input type="number" min="1" step="0.1" disabled={pay.overtimeRule === "none"} value={pay.overtimeMultiplier ?? 1.5} onChange={(e) => updatePay({ overtimeMultiplier: num(e.target.value) })} data-testid="staff-ot-mult" className={inpClass} />
          </div>
        </div>
      )}

      {showCommission && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Lbl>Commission (%)</Lbl>
            <input type="number" min="0" step="0.5" value={pay.commissionPct ?? 0} onChange={(e) => updatePay({ commissionPct: num(e.target.value) })} data-testid="staff-commission-pct" className={inpClass} />
          </div>
          <div>
            <Lbl>Commission base</Lbl>
            <select value={pay.commissionBase || "job-value"} onChange={(e) => updatePay({ commissionBase: e.target.value })} data-testid="staff-commission-base" className={selClass}>
              <option value="job-value">Job value (price charged)</option>
              <option value="invoice-paid">Invoice paid amount</option>
              <option value="profit">Profit (revenue − material)</option>
            </select>
          </div>
        </div>
      )}

      {pay.model === "flat" && (
        <div className="w-1/2">
          <Lbl>Flat rate per job ($)</Lbl>
          <input type="number" min="0" step="1" value={pay.flatRatePerJob ?? 0} onChange={(e) => updatePay({ flatRatePerJob: num(e.target.value) })} data-testid="staff-flat-rate" className={inpClass} />
        </div>
      )}

      <TimeOffBalances employeeId={employeeId} pay={pay} updatePay={updatePay} idPrefix="staff" />

      <PayProfileEditor
        value={profile.payProfile || {}}
        onChange={(pp) => setProfile((p) => ({ ...p, payProfile: pp }))}
        idPrefix="staff-payprof"
      />

      <div className="flex justify-end pt-1">
        <Button type="button" onClick={onSave} disabled={saving} data-testid="staff-save-pay" className="h-9 bg-blue-600 hover:bg-blue-700 text-white text-[13px]">
          <DollarSign className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save pay settings"}
        </Button>
      </div>
      <div className="text-[10.5px] text-slate-400">Clocked hours × base rate (with OT) plus commission flow into Reports → Payroll for this staff member.</div>
    </div>
  );
}

// Editable certifications list for staff accounts.
function AccountCertsEditor({ certs = [], onChange }) {
  const [name, setName] = useState("");
  const [expires, setExpires] = useState("");
  const [status, setStatus] = useState("Active");

  const add = () => {
    if (!name.trim()) { toast.error("Certification name is required"); return; }
    onChange([...certs, { id: `cert-${Date.now()}`, name: name.trim(), expires: expires || null, status }]);
    setName(""); setExpires(""); setStatus("Active");
  };
  const remove = (id) => onChange(certs.filter((c) => c.id !== id));

  return (
    <div className="space-y-3" data-testid="account-certs-editor">
      <div className="space-y-2">
        {certs.map((c) => {
          const sc = certStatusColors[c.status] || certStatusColors.Active;
          return (
            <div key={c.id} className="border border-slate-200 rounded-md p-3 flex items-center gap-3" data-testid={`account-cert-${c.id}`}>
              <div className="h-9 w-9 rounded-md bg-amber-50 flex items-center justify-center shrink-0">
                <Award className="h-4 w-4 text-amber-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-slate-900">{c.name}</div>
                <div className="text-[11.5px] text-slate-500">
                  {c.expires ? `Expires ${new Date(c.expires).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}` : "No expiry"}
                </div>
              </div>
              <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.text }}>{c.status}</span>
              <button onClick={() => remove(c.id)} data-testid={`account-cert-remove-${c.id}`} className="h-7 w-7 rounded-md hover:bg-red-50 flex items-center justify-center shrink-0">
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </button>
            </div>
          );
        })}
        {certs.length === 0 && <Empty text="No certifications on file." />}
      </div>

      <div className="border border-dashed border-slate-300 rounded-lg p-3 space-y-2.5" data-testid="account-cert-add">
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Add certification</div>
        <div className="grid grid-cols-2 gap-2.5">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. EPA 608" data-testid="account-cert-name" className={inpClass} />
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="account-cert-status" className={selClass}>
            <option value="Active">Active</option>
            <option value="Expiring">Expiring</option>
            <option value="Expired">Expired</option>
          </select>
        </div>
        <div className="flex items-center gap-2.5">
          <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="account-cert-expires" className={`${inpClass} flex-1`} />
          <Button type="button" onClick={add} data-testid="account-cert-add-btn" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px]">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      </div>
    </div>
  );
}


function AvailabilityTab({ tech, onUpdate }) {
  const update = (day, patch) => {
    onUpdate({ ...tech, schedule: { ...tech.schedule, [day]: { ...tech.schedule[day], ...patch } } });
  };
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-slate-600 mb-2">
        Set the days and hours this technician is available for dispatch. Toggle a day off to remove it from the schedule.
      </div>
      {DAYS.map((day) => {
        const s = tech.schedule[day] || { active: false, start: "", end: "" };
        return (
          <div key={day} className="border border-slate-200 rounded-md px-3 py-2.5 flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer w-24">
              <input
                type="checkbox"
                checked={s.active}
                onChange={(e) => update(day, { active: e.target.checked, start: e.target.checked ? s.start || "08:00" : "", end: e.target.checked ? s.end || "17:00" : "" })}
                className="h-4 w-4 rounded text-blue-600"
              />
              <span className={`text-[13px] font-semibold ${s.active ? "text-slate-900" : "text-slate-400"}`}>{day}</span>
            </label>
            <input
              type="time"
              value={s.start || ""}
              onChange={(e) => update(day, { start: e.target.value })}
              disabled={!s.active}
              className="h-8 px-2 rounded-md border border-slate-200 text-[12.5px] disabled:bg-slate-50 disabled:text-slate-300"
            />
            <span className="text-slate-400 text-[12px]">to</span>
            <input
              type="time"
              value={s.end || ""}
              onChange={(e) => update(day, { end: e.target.value })}
              disabled={!s.active}
              className="h-8 px-2 rounded-md border border-slate-200 text-[12.5px] disabled:bg-slate-50 disabled:text-slate-300"
            />
            {!s.active && <span className="ml-auto text-[11px] font-semibold text-slate-400">Off</span>}
          </div>
        );
      })}
    </div>
  );
}

function DaysOffTab({ tech, onUpdate }) {
  const [picker, setPicker] = useState("");
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthDays = [];
  const firstDay = monthStart.getDay();
  // Pad start
  for (let i = 0; i < firstDay; i++) monthDays.push(null);
  const last = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  for (let d = 1; d <= last; d++) monthDays.push(new Date(today.getFullYear(), today.getMonth(), d));

  const isOff = (d) => {
    if (!d) return false;
    const s = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return tech.daysOff.includes(s);
  };

  const toggle = (d) => {
    const s = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const next = tech.daysOff.includes(s) ? tech.daysOff.filter((x) => x !== s) : [...tech.daysOff, s].sort();
    onUpdate({ ...tech, daysOff: next });
  };

  const addPickedDate = () => {
    if (!picker) return;
    if (!tech.daysOff.includes(picker)) onUpdate({ ...tech, daysOff: [...tech.daysOff, picker].sort() });
    setPicker("");
  };

  return (
    <div className="space-y-4">
      <div className="text-[12px] text-slate-600">
        Click any date to mark/unmark as a day off. Days marked here will block dispatch on that date.
      </div>

      {/* Month grid */}
      <div>
        <div className="text-[13px] font-semibold text-slate-900 mb-2">
          {today.toLocaleDateString([], { month: "long", year: "numeric" })}
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10.5px] uppercase tracking-wider text-slate-500 mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {monthDays.map((d, i) => {
            if (!d) return <div key={i} />;
            const off = isOff(d);
            const isToday = d.toDateString() === today.toDateString();
            return (
              <button
                key={i}
                onClick={() => toggle(d)}
                className={`h-10 rounded-md text-[12.5px] font-medium transition-all border ${
                  off
                    ? "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                    : isToday
                      ? "border-blue-300 text-blue-700 bg-blue-50"
                      : "border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50"
                }`}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      {/* Picker for any future date */}
      <div className="border-t border-slate-100 pt-3">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Add a future date</div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={picker}
            onChange={(e) => setPicker(e.target.value)}
            className="h-9 px-2.5 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          />
          <Button onClick={addPickedDate} disabled={!picker} className="h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[12.5px]">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Day Off
          </Button>
        </div>
      </div>

      {/* Upcoming list */}
      <div className="border-t border-slate-100 pt-3">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">All Days Off ({tech.daysOff.length})</div>
        {tech.daysOff.length === 0 ? (
          <Empty text="No days off scheduled." />
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {tech.daysOff.map((d) => (
              <span key={d} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-50 border border-red-200 text-red-700 text-[12px] font-medium">
                {new Date(d + "T12:00").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                <button
                  onClick={() => onUpdate({ ...tech, daysOff: tech.daysOff.filter((x) => x !== d) })}
                  className="hover:bg-red-100 rounded p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function deriveCertStatus(expires) {
  if (!expires) return "Active";
  const exp = new Date(expires);
  if (isNaN(exp.getTime())) return "Active";
  const days = (exp - new Date()) / (1000 * 60 * 60 * 24);
  if (days < 0) return "Expired";
  return days <= 60 ? "Expiring Soon" : "Active";
}

function CertsTab({ tech, onUpdate }) {
  const [name, setName] = useState("");
  const [expires, setExpires] = useState("");
  const certs = tech.certifications || [];

  const add = () => {
    if (!name.trim()) { toast.error("Certification name is required"); return; }
    const next = [
      ...certs,
      { id: `cert-${Date.now()}`, name: name.trim(), expires: expires || "", status: deriveCertStatus(expires) },
    ];
    onUpdate({ ...tech, certifications: next });
    setName(""); setExpires("");
    toast.success("Certification added");
  };
  const remove = (id) => onUpdate({ ...tech, certifications: certs.filter((c) => c.id !== id) });

  return (
    <div className="space-y-3" data-testid="certs-tab">
      <div className="space-y-2">
        {certs.map((c) => {
          const sc = certStatusColors[c.status] || certStatusColors.Active;
          return (
            <div key={c.id} className="border border-slate-200 rounded-md p-3 flex items-center gap-3" data-testid={`cert-row-${c.id}`}>
              <div className="h-9 w-9 rounded-md bg-amber-50 flex items-center justify-center shrink-0">
                <Award className="h-4 w-4 text-amber-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-slate-900">{c.name}</div>
                <div className="text-[11.5px] text-slate-500">
                  {c.expires ? `Expires ${new Date(c.expires).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}` : "No expiry"}
                </div>
              </div>
              <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.text }}>{c.status}</span>
              <button onClick={() => remove(c.id)} data-testid={`cert-remove-${c.id}`} className="h-7 w-7 rounded-md hover:bg-red-50 flex items-center justify-center shrink-0">
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </button>
            </div>
          );
        })}
        {certs.length === 0 && <Empty text="No certifications on file." />}
      </div>

      <div className="border border-dashed border-slate-300 rounded-lg p-3 space-y-2.5" data-testid="cert-add">
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Add certification</div>
        <div>
          <label className="block text-[10.5px] uppercase tracking-wide text-slate-400 mb-1">Certification name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. EPA Section 608 Universal" data-testid="cert-name" className={`${inpClass} w-full`} />
        </div>
        <div className="flex items-end gap-2.5">
          <div className="flex-1">
            <label className="block text-[10.5px] uppercase tracking-wide text-slate-400 mb-1">Expiration date</label>
            <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="cert-expires" className={`${inpClass} w-full`} />
          </div>
          <Button type="button" onClick={add} data-testid="cert-add-btn" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px]">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      </div>
    </div>
  );
}

function ProfileSection({ tech, onUpdate, readOnly, linkedUser, onLinkedUserChange }) {
  const avatarInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [name, setName] = useState(tech.name || "");
  const [phone, setPhone] = useState(tech.phone || "");
  const [email, setEmail] = useState(tech.email || "");
  const [savingContact, setSavingContact] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    (async () => {
      await Promise.resolve();
      setName(tech.name || "");
      setPhone(tech.phone || "");
      setEmail(tech.email || "");
    })();
  }, [tech.id, tech.name, tech.phone, tech.email]);

  const detailsDirty =
    name !== (tech.name || "") || phone !== (tech.phone || "") || email !== (tech.email || "");
  const documents = tech.documents || [];

  const saveDetails = async () => {
    if (!name.trim()) {
      toast.error("Name can't be empty");
      return;
    }
    setSavingContact(true);
    try {
      await onUpdate({ ...tech, name: name.trim(), phone: phone.trim(), email: email.trim() }, true);
      // Keep the linked login account's name in sync with the staff card name.
      if (linkedUser && name.trim() && name.trim() !== linkedUser.name) {
        try {
          const u = await usersApi.update(linkedUser.id, { name: name.trim() });
          onLinkedUserChange?.(u);
        } catch (e) {
          /* non-fatal: staff card name still saved */
        }
      }
      toast.success("Details saved");
    } finally {
      setSavingContact(false);
    }
  };

  const onAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploadingAvatar(true);
    try {
      const updated = await techniciansApi.uploadAvatar(tech.id, file);
      await onUpdate(updated, false);
      toast.success("Profile image updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image upload failed");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const onDocPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingDoc(true);
    try {
      const updated = await techniciansApi.uploadDocument(tech.id, file, file.name);
      await onUpdate(updated, false);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Document upload failed");
    } finally {
      setUploadingDoc(false);
    }
  };

  const removeDoc = async (docId) => {
    setDeletingId(docId);
    try {
      const updated = await techniciansApi.deleteDocument(tech.id, docId);
      await onUpdate(updated, false);
      toast.success("Document removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove document");
    } finally {
      setDeletingId(null);
    }
  };

  const inputCls =
    "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-slate-50 disabled:text-slate-400";

  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-4" data-testid="profile-section">
      {/* Profile image + contact */}
      <div className="flex items-start gap-4">
        <div className="shrink-0 text-center">
          <div
            className="h-16 w-16 rounded-xl text-white text-[18px] font-bold flex items-center justify-center overflow-hidden mx-auto"
            style={{ background: tech.color }}
          >
            {tech.avatarUrl ? (
              <img src={techniciansApi.avatarUrl(tech)} alt={tech.name} className="h-full w-full object-cover" />
            ) : (
              tech.avatar
            )}
          </div>
          {!readOnly && (
            <button
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploadingAvatar}
              data-testid="upload-avatar-btn"
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-60"
            >
              {uploadingAvatar ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
              {uploadingAvatar ? "Uploading…" : "Upload image"}
            </button>
          )}
          <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={onAvatarPick} />
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-0.5">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={readOnly}
              data-testid="profile-name-input"
              placeholder="Full name"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-0.5">Phone</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={readOnly}
              data-testid="profile-phone-input"
              placeholder="(555) 000-0000"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-0.5">Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={readOnly}
              data-testid="profile-email-input"
              placeholder="name@company.com"
              className={inputCls}
            />
          </div>
          {!readOnly && (
            <button
              onClick={saveDetails}
              disabled={!detailsDirty || savingContact}
              data-testid="profile-save-contact-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-blue-600 text-white text-[12px] font-semibold disabled:bg-slate-200 disabled:text-slate-500"
            >
              {savingContact ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              Save details
            </button>
          )}
        </div>
      </div>

      {/* Documents */}
      <div className="border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700">
            <FileText className="h-3.5 w-3.5 text-slate-400" /> Documents
            <span className="text-[11px] font-normal text-slate-400">contracts, tax forms, license…</span>
          </div>
          {!readOnly && (
            <button
              onClick={() => docInputRef.current?.click()}
              disabled={uploadingDoc}
              data-testid="upload-doc-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-slate-900 text-white text-[12px] font-semibold disabled:opacity-60"
            >
              {uploadingDoc ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Upload Docs
            </button>
          )}
          <input
            ref={docInputRef}
            type="file"
            accept="image/*,application/pdf,.doc,.docx,.csv,.txt"
            hidden
            onChange={onDocPick}
          />
        </div>

        {documents.length === 0 ? (
          <div className="text-[12px] text-slate-400 py-2">No documents uploaded yet.</div>
        ) : (
          <ul className="space-y-1.5" data-testid="documents-list">
            {documents.map((d) => (
              <li
                key={d.id}
                data-testid={`document-row-${d.id}`}
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white hover:border-blue-300 transition-colors"
              >
                <a
                  href={techniciansApi.documentUrl(tech.id, d.id)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`document-link-${d.id}`}
                  className="flex items-center gap-2 min-w-0 text-[13px] text-slate-700 hover:text-blue-700 font-medium"
                >
                  <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                  <span className="truncate">{d.title}</span>
                  <ExternalLink className="h-3 w-3 text-slate-300 shrink-0" />
                </a>
                {!readOnly && (
                  <button
                    onClick={() => removeDoc(d.id)}
                    disabled={deletingId === d.id}
                    data-testid={`document-delete-${d.id}`}
                    aria-label="Delete document"
                    className="shrink-0 w-7 h-7 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-500 flex items-center justify-center disabled:opacity-50"
                  >
                    {deletingId === d.id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PayrollTab({ tech, onUpdate }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [titles, setTitles] = useState([]);
  const [linkedUser, setLinkedUser] = useState(null); // {id, email, role} | null | undefined (loading)
  const [savingRole, setSavingRole] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [removingLogin, setRemovingLogin] = useState(false);
  const [pendingInvite, setPendingInvite] = useState(null); // pending (unaccepted) invite | null
  const [resending, setResending] = useState(false);
  const initial = useMemo(() => ({
    jobTitleId: tech.jobTitleId || "",
    payProfile: tech.payProfile || {},
    qualifications: tech.qualifications || {},
    pay: {
      model: tech.pay?.model || "hourly",
      hourlyRate: tech.pay?.hourlyRate ?? 0,
      overtimeRule: tech.pay?.overtimeRule || "40wk",
      overtimeMultiplier: tech.pay?.overtimeMultiplier ?? 1.5,
      doubleTimeMultiplier: tech.pay?.doubleTimeMultiplier ?? 2.0,
      commissionPct: tech.pay?.commissionPct ?? 0,
      commissionBase: tech.pay?.commissionBase || "job-value",
      tradeOverrides: tech.pay?.tradeOverrides || [],
      flatRatePerJob: tech.pay?.flatRatePerJob ?? 0,
      annualSalary: tech.pay?.annualSalary ?? 0,
      payFrequency: tech.pay?.payFrequency || "biweekly",
      ptoBalanceHours: tech.pay?.ptoBalanceHours ?? 0,
      sickBalanceHours: tech.pay?.sickBalanceHours ?? 0,
    },
  }), [tech]);
  const [form, setForm] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      await Promise.resolve();
      setForm(initial);
      setDirty(false);
    })();
  }, [initial]);

  useEffect(() => {
    (async () => {
      try {
        setTitles(await jobTitlesApi.list());
      } catch {
        setTitles([]);
      }
    })();
  }, []);

  const [tradeTypes, setTradeTypes] = useState([]);
  useEffect(() => {
    tradeTypesApi.get().then((d) => setTradeTypes(d.trades || [])).catch(() => setTradeTypes([]));
  }, []);

  // Find the linked user account by email so admins can change role inline.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      if (!isAdmin || !tech.email) { setLinkedUser(null); return; }
      try {
        const users = await usersApi.list();
        if (cancelled) return;
        const match = (users || []).find((u) => (u.email || "").toLowerCase() === tech.email.toLowerCase());
        setLinkedUser(match || null);
      } catch {
        if (!cancelled) setLinkedUser(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, tech.email]);

  // Find a pending (sent, not-yet-accepted) invite for this email, so we can
  // offer a "Resend Invite" action when there's no login account yet.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      if (!isAdmin || !tech.email) { setPendingInvite(null); return; }
      try {
        const list = await invitesApi.list();
        if (cancelled) return;
        const match = (list || []).find((i) => (i.email || "").toLowerCase() === tech.email.toLowerCase());
        setPendingInvite(match || null);
      } catch {
        if (!cancelled) setPendingInvite(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, tech.email]);

  const resendInvite = async () => {
    if (!pendingInvite) return;
    setResending(true);
    try {
      const updated = await invitesApi.resend(pendingInvite.id);
      setPendingInvite(updated);
      toast.success(`Invite re-sent to ${pendingInvite.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend invite");
    } finally {
      setResending(false);
    }
  };

  const updateRole = async (nextRole) => {
    if (!linkedUser || linkedUser.role === nextRole) return;
    setSavingRole(true);
    try {
      const updated = await usersApi.update(linkedUser.id, { role: nextRole });
      setLinkedUser(updated);
      toast.success(`Permission set to ${nextRole.replace("_", " ")}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not change permission");
    } finally {
      setSavingRole(false);
    }
  };

  const resetPassword = async () => {
    if (!linkedUser || newPw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSavingPw(true);
    try {
      await usersApi.update(linkedUser.id, { password: newPw });
      toast.success(`Password reset for ${linkedUser.email}`);
      setNewPw("");
      setPwOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setSavingPw(false);
    }
  };

  const removeLogin = async () => {
    if (!linkedUser) return;
    if (!window.confirm(`Remove the login account for "${linkedUser.email}"? The technician card stays, but they can no longer sign in.`)) return;
    setRemovingLogin(true);
    try {
      await usersApi.remove(linkedUser.id);
      setLinkedUser(null);
      toast.success("Login account removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove login");
      setRemovingLogin(false);
    }
  };

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  };
  const updatePay = (patch) => {
    setForm((f) => ({ ...f, pay: { ...f.pay, ...patch } }));
    setDirty(true);
  };
  const updateQualification = (trade, patch) => {
    setForm((f) => ({
      ...f,
      qualifications: {
        ...f.qualifications,
        [trade]: { ...(f.qualifications?.[trade] || { active: false, subCategories: [] }), ...patch },
      },
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await techniciansApi.update(tech.id, {
        jobTitleId: form.jobTitleId || null,
        pay: form.pay,
        payProfile: form.payProfile || {},
        qualifications: form.qualifications || {},
      });
      onUpdate(updated);
      setDirty(false);
      toast.success("Pay settings saved");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addOverride = () => {
    updatePay({ tradeOverrides: [...form.pay.tradeOverrides, { trade: "HVAC", pct: 0 }] });
  };
  const removeOverride = (i) => {
    updatePay({ tradeOverrides: form.pay.tradeOverrides.filter((_, idx) => idx !== i) });
  };
  const editOverride = (i, patch) => {
    const next = form.pay.tradeOverrides.map((o, idx) => (idx === i ? { ...o, ...patch } : o));
    updatePay({ tradeOverrides: next });
  };

  const ro = !isAdmin;
  const TRADES = ["HVAC", "Plumbing", "Electrical", "Gas", "Other"];
  const showHourly = form.pay.model === "hourly" || form.pay.model === "hybrid";
  const showCommission = form.pay.model === "commission" || form.pay.model === "hybrid";
  const showFlat = form.pay.model === "flat";
  const showSalary = form.pay.model === "salary";

  return (
    <div className="space-y-3" data-testid="pay-tab">
      <ProfileSection
        tech={tech}
        onUpdate={onUpdate}
        readOnly={ro}
        linkedUser={linkedUser}
        onLinkedUserChange={setLinkedUser}
      />

      {ro && (
        <div className="px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[12px] text-amber-800">
          Read-only — only an admin can edit pay settings.
        </div>
      )}

      {/* Permissions */}
      {isAdmin && (
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40" data-testid="permissions-card">
          <Lbl>Permissions</Lbl>
          <RoleButtons
            value={linkedUser?.role}
            onChange={updateRole}
            disabled={savingRole || !linkedUser}
            testidPrefix="perm"
          />
          {linkedUser === null ? (
            pendingInvite ? (
              <div className="mt-2" data-testid="perm-pending-invite">
                <div className="flex items-center gap-2">
                  <button
                    onClick={resendInvite}
                    disabled={resending}
                    data-testid="perm-resend-invite"
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-blue-600 text-white text-[11.5px] font-semibold hover:bg-blue-700 disabled:opacity-60"
                  >
                    {resending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    {resending ? "Sending…" : "Resend Invite"}
                  </button>
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                    Invited — awaiting acceptance
                  </span>
                </div>
                <div className="mt-1.5 text-[11px] text-slate-500">
                  Sent to <span className="font-mono">{pendingInvite.email}</span>
                </div>
              </div>
            ) : (
              <div className="mt-1.5 text-[11px] text-slate-500 italic" data-testid="perm-no-login-hint">
                No user account is linked to <span className="font-mono">{tech.email || "(no email)"}</span>.
                {tech.email && " Use Add Staff above to create one."}
              </div>
            )
          ) : (
            <>
              <div className="mt-1.5 text-[11px] text-slate-500">
                Linked user: <span className="font-mono">{linkedUser.email}</span>
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                <button
                  onClick={() => setPwOpen((v) => !v)}
                  data-testid="perm-reset-password-toggle"
                  className="h-7 px-2.5 rounded-md border border-slate-200 bg-white text-[11.5px] font-semibold text-slate-700 hover:border-blue-300 hover:bg-blue-50/40 hover:text-blue-700"
                >
                  Reset password
                </button>
                <button
                  onClick={removeLogin}
                  disabled={removingLogin}
                  data-testid="perm-remove-login"
                  className="h-7 px-2.5 rounded-md border border-slate-200 bg-white text-[11.5px] font-semibold text-red-600 hover:bg-red-50 hover:border-red-300 disabled:opacity-40"
                >
                  {removingLogin ? "Removing…" : "Remove login"}
                </button>
              </div>
              {pwOpen && (
                <div className="mt-2 flex items-center gap-2" data-testid="perm-reset-password-row">
                  <input
                    type="password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="New password (min 6 chars)"
                    data-testid="perm-new-password"
                    className="flex-1 h-8 px-2.5 rounded-md border border-slate-200 bg-white text-[12.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                  />
                  <Button
                    onClick={resetPassword}
                    disabled={savingPw || newPw.length < 6}
                    data-testid="perm-save-password"
                    className="h-8 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[12px]"
                  >
                    {savingPw ? "Saving…" : "Save"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {/* Job title */}
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40">
        <Lbl>Job title</Lbl>
        <select
          value={form.jobTitleId}
          onChange={(e) => update({ jobTitleId: e.target.value })}
          disabled={ro}
          data-testid="pay-job-title"
          className={selClass}
        >
          <option value="">(none)</option>
          {titles.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {titles.length === 0 && (
          <div className="mt-1 text-[11px] text-slate-500">No titles yet — add them under Company Settings → Job titles.</div>
        )}
      </div>

      {/* Qualifications: trade + sub-category approvals used by Assign Technician filtering */}
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40" data-testid="staff-qualifications-section">
        <Lbl>Qualifications</Lbl>
        <div className="grid grid-cols-2 gap-2 mt-1">
          {tradeTypes.map((tt) => {
            const trade = tt.name;
            const q = form.qualifications?.[trade] || { active: false, subCategories: [] };
            return (
              <div key={trade} className="col-span-1">
                <button
                  type="button"
                  onClick={() => !ro && updateQualification(trade, { active: !q.active })}
                  disabled={ro}
                  data-testid={`qual-trade-toggle-${trade}`}
                  className={`w-full h-9 rounded-md text-[12.5px] font-semibold transition-colors ${
                    q.active ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400"
                  } disabled:opacity-70`}
                >
                  {trade}
                </button>
                {q.active && (
                  <div className="mt-1.5 border border-slate-200 rounded-md bg-white p-2 space-y-1" data-testid={`qual-subcats-${trade}`}>
                    {(tt.subCategories || []).map((sub) => {
                      const checked = (q.subCategories || []).includes(sub);
                      return (
                        <label key={sub} className="flex items-center gap-2 text-[11.5px] text-slate-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={ro}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...(q.subCategories || []), sub]
                                : (q.subCategories || []).filter((s) => s !== sub);
                              updateQualification(trade, { subCategories: next });
                            }}
                            data-testid={`qual-subcat-${trade}-${sub.replace(/\s+/g, "")}`}
                            className="h-3.5 w-3.5 rounded text-blue-600"
                          />
                          {sub}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          Used to filter which technicians can be assigned to a job based on its trade and job type.
        </div>
      </div>

      {/* Compensation model */}
      <div className="border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Lbl>Compensation model</Lbl>
            <select
              value={form.pay.model}
              onChange={(e) => updatePay({ model: e.target.value })}
              disabled={ro}
              data-testid="pay-model"
              className={selClass}
            >
              <option value="hourly">Hourly only</option>
              <option value="commission">Commission only</option>
              <option value="hybrid">Hybrid (hourly + commission)</option>
              <option value="flat">Per-job flat rate</option>
              <option value="piece_rate">Piece rate (per unit)</option>
              <option value="salary">Salary</option>
            </select>
          </div>
          <div>
            <Lbl>Overtime rule</Lbl>
            <select
              value={form.pay.overtimeRule}
              onChange={(e) => updatePay({ overtimeRule: e.target.value })}
              disabled={ro || !showHourly}
              data-testid="pay-ot-rule"
              className={selClass}
            >
              <option value="40wk">1.5× over 40 hrs/week</option>
              <option value="ca-daily">CA: 1.5× &gt;8/day, 2× &gt;12/day</option>
              <option value="none">No overtime</option>
            </select>
          </div>
        </div>

        {showHourly && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Lbl>Base hourly rate ($)</Lbl>
              <input
                type="number" step="0.01" min="0"
                value={form.pay.hourlyRate}
                onChange={(e) => updatePay({ hourlyRate: parseFloat(e.target.value) || 0 })}
                disabled={ro}
                data-testid="pay-hourly"
                className={inpClass}
              />
            </div>
            <div>
              <Lbl>OT multiplier ({form.pay.overtimeRule === "ca-daily" ? "1.5× standard / 2× DT" : "applied"})</Lbl>
              <input
                type="number" step="0.1" min="1" max="3"
                value={form.pay.overtimeMultiplier}
                onChange={(e) => updatePay({ overtimeMultiplier: parseFloat(e.target.value) || 1.5 })}
                disabled={ro || form.pay.overtimeRule === "none"}
                className={inpClass}
              />
            </div>
          </div>
        )}

        {showCommission && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Lbl>Default commission %</Lbl>
                <input
                  type="number" step="0.1" min="0" max="100"
                  value={form.pay.commissionPct}
                  onChange={(e) => updatePay({ commissionPct: parseFloat(e.target.value) || 0 })}
                  disabled={ro}
                  data-testid="pay-comm-pct"
                  className={inpClass}
                />
              </div>
              <div>
                <Lbl>Commission base</Lbl>
                <select
                  value={form.pay.commissionBase}
                  onChange={(e) => updatePay({ commissionBase: e.target.value })}
                  disabled={ro}
                  data-testid="pay-comm-base"
                  className={selClass}
                >
                  <option value="job-value">Job value (price charged)</option>
                  <option value="invoice-paid">Invoice paid amount</option>
                  <option value="profit">Profit (revenue − material cost)</option>
                </select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Lbl>Per-trade overrides (optional)</Lbl>
                {!ro && (
                  <button onClick={addOverride} className="h-7 px-2 rounded text-[11.5px] font-semibold border border-slate-200 hover:bg-slate-50 flex items-center gap-1">
                    <Plus className="h-3 w-3" /> Add trade
                  </button>
                )}
              </div>
              {form.pay.tradeOverrides.length === 0 ? (
                <div className="text-[11.5px] text-slate-400 italic">No overrides — the default commission % applies to every trade.</div>
              ) : (
                <div className="space-y-1.5">
                  {form.pay.tradeOverrides.map((o, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`pay-trade-${i}`}>
                      <select
                        value={o.trade}
                        onChange={(e) => editOverride(i, { trade: e.target.value })}
                        disabled={ro}
                        className={`${selClass} w-40`}
                      >
                        {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <input
                        type="number" step="0.1" min="0" max="100"
                        value={o.pct}
                        onChange={(e) => editOverride(i, { pct: parseFloat(e.target.value) || 0 })}
                        disabled={ro}
                        className={`${inpClass} w-24`}
                      />
                      <span className="text-[12px] text-slate-500">%</span>
                      {!ro && (
                        <button onClick={() => removeOverride(i)} className="h-7 w-7 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-600 flex items-center justify-center">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {showFlat && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Lbl>Flat rate per completed job ($)</Lbl>
              <input
                type="number" step="0.01" min="0"
                value={form.pay.flatRatePerJob}
                onChange={(e) => updatePay({ flatRatePerJob: parseFloat(e.target.value) || 0 })}
                disabled={ro}
                data-testid="pay-flat-rate"
                className={inpClass}
              />
            </div>
          </div>
        )}

        {form.pay.model === "piece_rate" && (
          <div className="text-[11.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            Piece-rate pay is calculated automatically from this tech's completed jobs' invoice line items that use a Pricebook item with a per-unit rate set. Set rates on the <span className="font-semibold">Pricebook</span> page.
          </div>
        )}

        {showSalary && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Lbl>Annual salary ($)</Lbl>
              <input
                type="number" step="100" min="0"
                value={form.pay.annualSalary}
                onChange={(e) => updatePay({ annualSalary: parseFloat(e.target.value) || 0 })}
                disabled={ro}
                data-testid="pay-annual-salary"
                className={inpClass}
              />
            </div>
            <div>
              <Lbl>Pay frequency</Lbl>
              <select
                value={form.pay.payFrequency}
                onChange={(e) => updatePay({ payFrequency: e.target.value })}
                disabled={ro}
                data-testid="pay-frequency"
                className={selClass}
              >
                {PAY_FREQUENCIES.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="border border-slate-200 rounded-lg p-4">
        <TimeOffBalances employeeId={tech.id} pay={form.pay} updatePay={updatePay} disabled={ro} idPrefix="tech" />
      </div>

      {isAdmin && (
        <PayProfileEditor
          value={form.payProfile}
          onChange={(pp) => update({ payProfile: pp })}
          disabled={ro}
          idPrefix="tech-payprof"
        />
      )}

      {!ro && (
        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="pay-save"
            className="h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : "Save Pay Settings"}
          </Button>
        </div>
      )}

      {/* Dedicated height-based spacer (not padding — theme CSS overrides
          padding) so the Save button clears the fixed mobile bottom nav,
          plus two full extra rows (4rem each) of breathing room below it. */}
      <div aria-hidden="true" className="md:hidden shrink-0" style={{ height: "calc(5rem + 8rem + env(safe-area-inset-bottom))" }} />
    </div>
  );
}

const inpClass = "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-slate-50 disabled:text-slate-400";
const selClass = "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-slate-50 disabled:text-slate-400";

function Lbl({ children }) {
  return <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{children}</label>;
}

function Empty({ text }) {
  return <div className="text-center py-6 text-[12.5px] text-slate-400">{text}</div>;
}

// Editable pay-stub / tax profile (home address, marital status, exemptions,
// SSN last-4, employee ID + Federal/State withholding and custom deductions).
// FICA is auto-computed server-side. Reused for both staff accounts + technicians.
function PayProfileEditor({ value = {}, onChange, disabled = false, idPrefix = "payprof" }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const customs = v.customDeductions || [];
  const setCustom = (i, patch) => set({ customDeductions: customs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const addCustom = () => set({ customDeductions: [...customs, { label: "", type: "flat", value: 0 }] });
  const removeCustom = (i) => set({ customDeductions: customs.filter((_, idx) => idx !== i) });
  const num = (x) => (x === "" || x === null || x === undefined ? 0 : Number(x));
  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid={`${idPrefix}-editor`}>
      <div className="text-[12px] font-bold uppercase tracking-wide text-slate-400">Pay-stub / tax profile</div>
      <div>
        <Lbl>Home address</Lbl>
        <input value={v.homeAddress || ""} onChange={(e) => set({ homeAddress: e.target.value })} disabled={disabled} placeholder="123 N. McAllister Ave, Tempe, AZ 85281" className={inpClass} data-testid={`${idPrefix}-address`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Lbl>Marital status</Lbl>
          <select value={v.maritalStatus || ""} onChange={(e) => set({ maritalStatus: e.target.value })} disabled={disabled} className={selClass} data-testid={`${idPrefix}-marital`}>
            <option value="">—</option>
            <option value="Single">Single</option>
            <option value="Married">Married</option>
            <option value="Head of Household">Head of Household</option>
          </select>
        </div>
        <div>
          <Lbl>Exemptions</Lbl>
          <input type="number" min="0" step="1" value={v.exemptions ?? ""} onChange={(e) => set({ exemptions: num(e.target.value) })} disabled={disabled} className={inpClass} data-testid={`${idPrefix}-exemptions`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Lbl>SSN (last 4)</Lbl>
          <input maxLength={4} value={v.ssnLast4 || ""} onChange={(e) => set({ ssnLast4: e.target.value.replace(/\D/g, "").slice(0, 4) })} disabled={disabled} placeholder="1234" className={inpClass} data-testid={`${idPrefix}-ssn`} />
        </div>
        <div>
          <Lbl>Employee ID</Lbl>
          <input value={v.employeeId || ""} onChange={(e) => set({ employeeId: e.target.value })} disabled={disabled} placeholder="DC002023" className={inpClass} data-testid={`${idPrefix}-empid`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Lbl>Federal withholding</Lbl>
          <div className="flex gap-2">
            <select value={v.federalType || "pct"} onChange={(e) => set({ federalType: e.target.value })} disabled={disabled} className={`${selClass} w-16`} data-testid={`${idPrefix}-fed-type`}>
              <option value="pct">%</option>
              <option value="flat">$</option>
            </select>
            <input type="number" min="0" step="0.01" value={v.federalValue ?? 0} onChange={(e) => set({ federalValue: num(e.target.value) })} disabled={disabled} className={inpClass} data-testid={`${idPrefix}-fed-value`} />
          </div>
        </div>
        <div>
          <Lbl>State withholding</Lbl>
          <div className="flex gap-2">
            <select value={v.stateType || "pct"} onChange={(e) => set({ stateType: e.target.value })} disabled={disabled} className={`${selClass} w-16`} data-testid={`${idPrefix}-state-type`}>
              <option value="pct">%</option>
              <option value="flat">$</option>
            </select>
            <input type="number" min="0" step="0.01" value={v.stateValue ?? 0} onChange={(e) => set({ stateValue: num(e.target.value) })} disabled={disabled} className={inpClass} data-testid={`${idPrefix}-state-value`} />
          </div>
        </div>
      </div>
      <div>
        <Lbl>State tax label</Lbl>
        <input value={v.stateLabel || ""} onChange={(e) => set({ stateLabel: e.target.value })} disabled={disabled} placeholder="AZ ST Tax" className={inpClass} data-testid={`${idPrefix}-state-label`} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Lbl>Custom deductions (optional)</Lbl>
          {!disabled && (
            <button type="button" onClick={addCustom} data-testid={`${idPrefix}-add-custom`} className="h-7 px-2 rounded text-[11.5px] font-semibold border border-slate-200 hover:bg-slate-50 flex items-center gap-1">
              <Plus className="h-3 w-3" /> Add
            </button>
          )}
        </div>
        {customs.length === 0 ? (
          <div className="text-[11.5px] text-slate-400 italic">None — e.g. health insurance, 401(k), garnishment.</div>
        ) : (
          <div className="space-y-1.5">
            {customs.map((c, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`${idPrefix}-custom-${i}`}>
                <input value={c.label || ""} onChange={(e) => setCustom(i, { label: e.target.value })} disabled={disabled} placeholder="Health Insurance" className={`${inpClass} flex-1`} />
                <select value={c.type || "flat"} onChange={(e) => setCustom(i, { type: e.target.value })} disabled={disabled} className={`${selClass} w-16`}>
                  <option value="flat">$</option>
                  <option value="pct">%</option>
                </select>
                <input type="number" min="0" step="0.01" value={c.value ?? 0} onChange={(e) => setCustom(i, { value: num(e.target.value) })} disabled={disabled} className={`${inpClass} w-24`} />
                {!disabled && (
                  <button type="button" onClick={() => removeCustom(i)} className="h-7 w-7 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-600 flex items-center justify-center">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="text-[10.5px] text-slate-400">FICA (SS 6.2% + Medicare 1.45%) is auto-applied server-side. These fields populate the employee's earnings-statement pay stub. Withholding is an estimate.</div>
    </div>
  );
}

